import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getSubmissionStatusColor, getSubmissionStatusLabel, getOutcomeConfig } from '../../utils/statusBadge';
import { formatSchedule } from '../../utils/schedule';
import {
  getNextUpdateInfo,
  fmtDur,
  DONE_DRAW_STATUSES,
  isOpenDraw,
} from '../../utils/submissionSla';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  useToast,
  Heading,
  Text,
  Select,
  HStack,
  Input,
  InputGroup,
  InputLeftElement,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  CloseButton,
  useColorModeValue,
  VStack,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Grid,
  GridItem,
  FormControl,
  FormLabel,
  SimpleGrid,
  IconButton,
  Wrap,
  WrapItem,
  Progress,
  Card,
  CardHeader,
  CardBody,
  Textarea,
  Spinner,
  Checkbox,
  Divider,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Tag,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverArrow,
} from '@chakra-ui/react';
import { SearchIcon, AttachmentIcon, ChevronDownIcon, ChevronUpIcon, EditIcon, DeleteIcon, CheckCircleIcon, CopyIcon } from '@chakra-ui/icons';
import { supabase } from '../../supabaseClient';
import { Link as RouterLink, useSearchParams, useNavigate } from 'react-router-dom';
import { FaCloudUploadAlt, FaShippingFast, FaTimes, FaSync, FaClone } from 'react-icons/fa';
import FedExLabelGenerator from '../shared/FedExLabelGenerator';
import { tagColor } from '../../utils/submissionTags';

import { getAuthHeaders } from '../../utils/apiAuth';
import { fetchAllRows } from '../../utils/fetchAllRows';
import { logAssignmentChange } from '../../utils/logOutcome';
function isImageFile(url) {
  return url && /\.(jpe?g|png|gif|bmp|webp|svg)$/i.test(url);
}

// Practices that are our own clients (their lab client codes). The "Our
// Clients" toggle narrows the list to just these.
const OUR_CLIENT_CODES = new Set(['6037', '3343', '3364', '3908', '4514', '5027', '4959', '5577']);

const PdfIcon = () => (
  <span
    style={{
      display: 'flex',
      width: 60,
      height: 60,
      background: '#f5f5f5',
      borderRadius: 6,
      border: '1px solid #ccc',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 32,
      color: '#e53e3e'
    }}
  >
    📄
  </span>
);

function PreviewModal({ isOpen, onClose, fileUrl }) {
  const isImage = isImageFile(fileUrl);
  const isPdf = fileUrl && fileUrl.endsWith('.pdf');
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="4xl" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalCloseButton />
        <ModalBody display="flex" alignItems="center" justifyContent="center" p={0}>
          {isImage && (
            <img src={fileUrl} alt="Preview" style={{ maxWidth: '90vw', maxHeight: '80vh', objectFit: 'contain' }} />
          )}
          {isPdf && (
            <iframe src={fileUrl} title="PDF Preview" style={{ width: '90vw', height: '80vh', border: 'none' }} />
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

const formatPatientName = (name) => {
  if (!name) return '';
  const trimmed = name.trim();
  if (trimmed.includes(',')) return trimmed;
  const words = trimmed.split(/\s+/);
  if (words.length <= 1) return trimmed;
  return words[words.length - 1] + ', ' + words.slice(0, -1).join(' ');
};

// The scheduled appointment as a local-date range [startYmd, endYmd] (strings,
// "YYYY-MM-DD") for filtering — compared the same way the Scheduled column is
// displayed, so exact times and windows both match without timezone drift.
// Returns null when the draw has no schedule set.
const localYmd = (value) => {
  if (!value) return null;
  const raw = String(value);
  // Already a plain date → use as-is (avoids UTC-midnight day shift).
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};
const getScheduledRange = (s) => {
  if (!s) return null;
  const type = s.scheduling_type
    || (s.availability_start_date ? 'window' : (s.blood_collection_time ? 'exact' : null));
  // Windows / all-day carry their own date range.
  if ((type === 'window' || type === 'all_day') && s.availability_start_date) {
    const start = localYmd(s.availability_start_date);
    const end = localYmd(s.availability_end_date || s.availability_start_date) || start;
    if (start) return { start, end };
  }
  // Exact (or anything with a collection time) → a single day.
  const day = localYmd(s.blood_collection_time);
  if (day) return { start: day, end: day };
  // Last resort: a window date with no type set.
  if (s.availability_start_date) {
    const start = localYmd(s.availability_start_date);
    const end = localYmd(s.availability_end_date || s.availability_start_date) || start;
    if (start) return { start, end };
  }
  return null;
};

// Classify how a submission entered the system using existing columns.
// - ldx_message_id is set ONLY by the LDX lab API sync (api/ldx-sync.js), so
//   orders pulled from the lab over the API show as "Lab API".
// - created_by_user is set by the manual entry forms with a prefix convention:
//     'DR-<name>'  → doctor portal
//     'FAC-<name>' → facility portal
//     'LAB'        → phlebotomist / mobile lab portal
//     anything else (a real name) → an admin typed it in the admin panel
const getSubmissionSource = (submission) => {
  if (submission?.ldx_message_id) {
    return { label: 'Lab API', colorScheme: 'purple', title: `From lab via API (${submission.ldx_message_id})` };
  }
  const createdBy = (submission?.created_by_user || '').trim();
  if (createdBy.toUpperCase().startsWith('DR-')) {
    return { label: 'Doctor', colorScheme: 'teal', title: `Added by ${createdBy.slice(3)}` };
  }
  if (createdBy.toUpperCase().startsWith('FAC-') || submission?.facility_id) {
    return { label: 'Facility', colorScheme: 'cyan', title: createdBy ? `Added by ${createdBy.replace(/^FAC-/i, '')}` : 'Added from facility portal' };
  }
  if (createdBy.toUpperCase() === 'LAB') {
    return { label: 'Phlebotomist', colorScheme: 'blue', title: 'Added from the mobile lab / phlebotomist portal' };
  }
  if (createdBy) {
    return { label: 'Admin', colorScheme: 'orange', title: `Added by ${createdBy}` };
  }
  return { label: 'Manual', colorScheme: 'gray', title: 'No creator recorded' };
};

// Next-update SLA, the DONE_DRAW_STATUSES set, and isOpenDraw now live in
// src/utils/submissionSla.js so the Daily Tasks dashboard shares the exact same
// "call patients" logic. Imported at the top of this file.

function SubmissionsList() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  // NOTE: keep this list in sync with SUBMISSION_STATUS_CONFIG in src/utils/statusBadge.js.
  // Any status not listed here defaults to VISIBLE (see filteredSubmissions below) so that
  // newly-introduced status codes don't silently disappear from the Blood Files page.
  const [statusFilters, setStatusFilters] = useState({
    pending: true,
    in_progress: true,
    completed: true,
    needs_reschedule: true,
    waiting_for_required_info: true,
    cancelled: false,
  });
  const [paymentFilter, setPaymentFilter] = useState('all'); // all, self_pay_unpaid, self_pay_paid, insurance
  const [overdueOnly, setOverdueOnly] = useState(false); // show only draws past their next-update SLA
  const [sortMode, setSortMode] = useState('priority'); // 'priority' | 'recent'
  const [dateFrom, setDateFrom] = useState(''); // YYYY-MM-DD inclusive
  const [dateTo, setDateTo] = useState('');     // YYYY-MM-DD inclusive
  const [dateField, setDateField] = useState('draw'); // 'draw' (submitted_at) | 'scheduled' (appointment)
  const [tagFilter, setTagFilter] = useState(null); // single tag to filter by, or null
  const [clientCodeFilter, setClientCodeFilter] = useState(null); // single practice client code, or null
  const [ourClientsOnly, setOurClientsOnly] = useState(false); // limit to OUR_CLIENT_CODES
  // submission_id -> earliest timestamp the draw reached a final status
  // (dropped off → waiting_on_lab_results, or completed). From status events.
  const [finalizedAt, setFinalizedAt] = useState({});
  const [showCompleted, setShowCompleted] = useState(false); // when a phleb is selected, also show their done draws
  const [, setNowTick] = useState(0); // ticks every minute so the Next Update countdown stays live
  const [phlebotomistIdFilter, setPhlebotomistIdFilter] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(25);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const toast = useToast();
  const [dismissedAlerts, setDismissedAlerts] = useState([]);
  const [topLabs, setTopLabs] = useState([]);
  const [labs, setLabs] = useState([]); // Keep as array for backward compatibility
  const [labsMap, setLabsMap] = useState({}); // Map of lab_id -> { id, name } for faster lookups
  const [phlebotomists, setPhlebotomists] = useState({}); // Map of phlebotomist_id -> { lab_draw_fee, ... }
  const [batches, setBatches] = useState([]); // All batches
  const [selectedSubmissions, setSelectedSubmissions] = useState(new Set()); // Selected submission IDs for batching
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isBatchModalOpen, onOpen: onBatchModalOpen, onClose: onBatchModalClose } = useDisclosure();
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [selectedLabId, setSelectedLabId] = useState('');
  const [selectedPhlebotomistId, setSelectedPhlebotomistId] = useState('');
  const [updatingPhleb, setUpdatingPhleb] = useState(false);
  const {
    isOpen: isPhlebModalOpen,
    onOpen: onPhlebModalOpen,
    onClose: onPhlebModalClose,
  } = useDisclosure();
  const [updatingLab, setUpdatingLab] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  
  const [editingForm, setEditingForm] = useState({});
  const [saving, setSaving] = useState(false);

  const [previewUrl, setPreviewUrl] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  
  // FedEx label generation state
  const [fedexModalOpen, setFedexModalOpen] = useState(false);
  const [selectedSubmissionForFedex, setSelectedSubmissionForFedex] = useState(null);

  // Inline date editing state
  const [editingDateId, setEditingDateId] = useState(null);
  const [editingDateValue, setEditingDateValue] = useState('');

  // Delete submission state
  const [submissionToDelete, setSubmissionToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const { isOpen: isDeleteDialogOpen, onOpen: onDeleteDialogOpen, onClose: onDeleteDialogClose } = useDisclosure();
  const cancelDeleteRef = useRef(null);

  // When each draw first reached a final status (dropped off / completed).
  // Pulled from the status-event history: the earliest event whose status is
  // 'waiting_on_lab_results' (dropped at lab) or 'completed' (results posted).
  const fetchFinalizedDates = async () => {
    try {
      const { data, error } = await supabase
        .from('submission_status_events')
        .select('submission_id, status, logged_at')
        .in('status', ['waiting_on_lab_results', 'completed'])
        .order('logged_at', { ascending: true });
      if (error) throw error;
      const map = {};
      (data || []).forEach((ev) => {
        if (ev.submission_id && !map[ev.submission_id]) map[ev.submission_id] = ev.logged_at; // first = earliest
      });
      setFinalizedAt(map);
    } catch (e) {
      console.warn('Could not load finalized dates:', e.message);
    }
  };

  useEffect(() => {
    fetchSubmissions();
    fetchLabs();
    fetchPhlebotomists();
    fetchBatches();
    fetchFinalizedDates();

    // Real-time sync: auto-update on any change (INSERT for new submissions,
    // UPDATE when the lab portal changes a status, DELETE when one is removed)
    // so admins never need to refresh the page.
    const channel = supabase
      .channel('submissions-realtime')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'submissions' }, () => {
        fetchSubmissions();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, []);

  // Tick every minute so the "Next Update" countdown / overdue badges update
  // live without needing a data refresh.
  useEffect(() => {
    const i = setInterval(() => setNowTick((n) => n + 1), 60 * 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    const validSubmissions = submissions.filter(sub => sub.phlebotomist_name && sub.phlebotomist_name !== 'Unknown' && sub.phlebotomist_name.trim() !== '');
    const labCounts = {};
    validSubmissions.forEach(sub => {
      const name = sub.phlebotomist_name;
      labCounts[name] = (labCounts[name] || 0) + 1;
    });
    const sortedLabs = Object.entries(labCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
    setTopLabs(sortedLabs);
  }, [submissions]);

  const fetchSubmissions = async () => {
    try {
      const data = await fetchAllRows(() => supabase
        .from('submissions')
        .select(`
          *,
          labs (
            id,
            name
          )
        `)
        .order('submitted_at', { ascending: false }));

      setSubmissions(data);
    } catch (error) {
      toast({
        title: 'Error fetching submissions',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initialize filters from URL query params (e.g. /admin/submissions?phlebotomistId=...&status=in_progress)
  useEffect(() => {
    const phlebIdFromUrl = searchParams.get('phlebotomistId');
    const statusFromUrl = searchParams.get('status');

    if (phlebIdFromUrl) {
      setPhlebotomistIdFilter(phlebIdFromUrl);
    }

    if (statusFromUrl) {
      if (statusFromUrl === 'all') {
        // Leave statusFilters as-is (show all)
        return;
      }
      setStatusFilters((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((key) => {
          next[key] = false;
        });
        if (next[statusFromUrl] !== undefined) {
          next[statusFromUrl] = true;
        }
        return next;
      });
    }
  }, [searchParams]);

  const fetchLabs = async () => {
    try {
      const { data, error } = await supabase
        .from('labs')
        .select('id, name');
      
      if (error) {
        console.error('Error fetching labs:', error);
        return;
      }
      
      const labsData = data || [];
      setLabs(labsData);
      
      // Create a map for faster lookups (handle UUID as both key formats)
      const labMap = {};
      labsData.forEach(lab => {
        const idStr = String(lab.id);
        labMap[lab.id] = lab; // Original format
        labMap[idStr] = lab; // String format
        // Also store lowercase for case-insensitive matching
        labMap[idStr.toLowerCase()] = lab;
      });
      setLabsMap(labMap);
      
      console.log('Labs loaded:', labsData.length, 'labs');
    } catch (error) {
      console.error('Error in fetchLabs:', error);
    }
  };

  const fetchPhlebotomists = async () => {
    try {
      // Include name fields so the per-row "Assign phleb" picker can show
      // something readable; lab_draw_fee is still here for the billing
      // tooltip and the per-row indicator.
      const { data, error } = await supabase
        .from('phlebotomist_profiles')
        .select('id, full_name, company_name, email, lab_draw_fee')
        .order('full_name', { ascending: true });

      if (error) throw error;

      // Create a map of phlebotomist_id -> phlebotomist data
      const phlebotomistMap = {};
      (data || []).forEach(phleb => {
        phlebotomistMap[phleb.id] = phleb;
      });
      setPhlebotomists(phlebotomistMap);
    } catch (error) {
      console.error('Error fetching phlebotomists:', error);
    }
  };

  // Generate unique batch number in format BATCH-YYYY-NNN (e.g., BATCH-2026-001)
  const generateBatchNumber = async () => {
    const year = new Date().getFullYear();
    const yearStr = year.toString();
    let seqNum = 1;
    let batchNumber;
    let exists = true;
    let attempts = 0;
    const maxAttempts = 999;

    while (exists && attempts < maxAttempts) {
      // Format: BATCH-YYYY-NNN (zero-padded to 3 digits)
      const seqStr = seqNum.toString().padStart(3, '0');
      batchNumber = `BATCH-${yearStr}-${seqStr}`;

      // Check if this batch number already exists
      const { data, error } = await supabase
        .from('submission_batches')
        .select('id')
        .eq('batch_number', batchNumber)
        .maybeSingle();

      if (error) {
        console.error('Error checking batch number:', error);
        break;
      }

      exists = data !== null;
      seqNum++;
      attempts++;
    }

    if (attempts >= maxAttempts) {
      console.error('Failed to generate unique batch number after', maxAttempts, 'attempts');
      // Fallback: use timestamp-based number
      batchNumber = `BATCH-${yearStr}-${Date.now().toString().slice(-3)}`;
    }

    return batchNumber;
  };

  const fetchBatches = async () => {
    try {
      const { data, error } = await supabase
        .from('submission_batches')
        .select('id, batch_name, batch_number, description, status, created_at')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setBatches(data || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const handleDateEdit = (submission, e) => {
    if (e) { e.stopPropagation(); }
    const currentDate = submission.submitted_at ? new Date(submission.submitted_at) : new Date();
    // Format as YYYY-MM-DD for date input, and MM/DD/YYYY for save
    const mm = String(currentDate.getMonth() + 1).padStart(2, '0');
    const dd = String(currentDate.getDate()).padStart(2, '0');
    const yyyy = currentDate.getFullYear();
    setEditingDateValue(`${yyyy}-${mm}-${dd}`);
    setEditingDateId(submission.id);
  };

  const handleDateSave = async (submissionId, e) => {
    if (e) { e.stopPropagation(); }
    try {
      // Parse MM/DD/YYYY format
      const parts = editingDateValue.match(/^(\d{1,2})\/?(\d{1,2})\/?(\d{4})$/);
      if (!parts) {
        toast({ title: 'Invalid date format', description: 'Use MM/DD/YYYY', status: 'warning', duration: 2000 });
        return;
      }
      const [, monthStr, dayStr, yearStr] = parts;
      const month = parseInt(monthStr, 10);
      const day = parseInt(dayStr, 10);
      const year = parseInt(yearStr, 10);
      if (month < 1 || month > 12 || day < 1 || day > 31 || year < 2000 || year > 2100) {
        toast({ title: 'Invalid date', description: 'Please enter a valid date', status: 'warning', duration: 2000 });
        return;
      }

      // Preserve the original time, just change the date
      const original = submissions.find(s => s.id === submissionId);
      const originalDate = original?.submitted_at ? new Date(original.submitted_at) : new Date();
      const newDate = new Date(originalDate);
      newDate.setFullYear(year, month - 1, day);

      // Sync blood_collection_time so invoices / lab views reflect the new draw date.
      // Preserve original time-of-day if one was already set; otherwise use noon UTC to avoid timezone shift.
      const newBloodCollection = original?.blood_collection_time
        ? (() => {
            const t = new Date(original.blood_collection_time);
            t.setUTCFullYear(year, month - 1, day);
            return t.toISOString();
          })()
        : new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();

      const { error } = await supabase
        .from('submissions')
        .update({ submitted_at: newDate.toISOString(), blood_collection_time: newBloodCollection })
        .eq('id', submissionId);

      if (error) throw error;

      // Also update linked invoice date if submission has an invoice_id
      if (original?.invoice_id) {
        await supabase
          .from('submission_invoices')
          .update({ created_at: newDate.toISOString() })
          .eq('id', original.invoice_id);
      }

      setSubmissions(prev => prev.map(s =>
        s.id === submissionId ? { ...s, submitted_at: newDate.toISOString(), blood_collection_time: newBloodCollection } : s
      ));
      toast({ title: 'Date updated', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Failed to update date', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setEditingDateId(null);
      setEditingDateValue('');
    }
  };

  const handleDateSaveDirect = async (submissionId, month, day, year) => {
    try {
      const original = submissions.find(s => s.id === submissionId);
      const originalDate = original?.submitted_at ? new Date(original.submitted_at) : new Date();
      const newDate = new Date(originalDate);
      newDate.setFullYear(year, month - 1, day);

      const newBloodCollection = original?.blood_collection_time
        ? (() => {
            const t = new Date(original.blood_collection_time);
            t.setUTCFullYear(year, month - 1, day);
            return t.toISOString();
          })()
        : new Date(Date.UTC(year, month - 1, day, 12, 0, 0)).toISOString();

      const { error } = await supabase
        .from('submissions')
        .update({ submitted_at: newDate.toISOString(), blood_collection_time: newBloodCollection })
        .eq('id', submissionId);

      if (error) throw error;

      if (original?.invoice_id) {
        await supabase
          .from('submission_invoices')
          .update({ created_at: newDate.toISOString() })
          .eq('id', original.invoice_id);
      }

      setSubmissions(prev => prev.map(s =>
        s.id === submissionId ? { ...s, submitted_at: newDate.toISOString(), blood_collection_time: newBloodCollection } : s
      ));
      toast({ title: 'Date updated', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Failed to update date', description: err.message, status: 'error', duration: 3000 });
    } finally {
      setEditingDateId(null);
      setEditingDateValue('');
    }
  };

  const handleDateCancel = (e) => {
    if (e) { e.stopPropagation(); }
    setEditingDateId(null);
    setEditingDateValue('');
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ status: newStatus })
        .eq('id', id);

      if (error) throw error;

      setSubmissions(submissions.map(sub => 
        sub.id === id ? { ...sub, status: newStatus } : sub
      ));

      toast({
        title: 'Status updated',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error updating status',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleFedexLabelUpload = async (submissionId, file) => {
    if (!file) return;
    const fileExt = file.name.split('.').pop();
    const filePath = `fedex-labels/${submissionId}.${fileExt}`;
    const { error: uploadError } = await supabase.storage.from('fedex-labels').upload(filePath, file, { upsert: true });
    if (uploadError) {
      toast({ title: 'Upload failed', description: uploadError.message, status: 'error', duration: 5000, isClosable: true });
      return;
    }
    const { data: urlData } = supabase.storage.from('fedex-labels').getPublicUrl(filePath);
    const labelUrl = urlData.publicUrl;
    const { error: updateError } = await supabase.from('submissions').update({ fedex_label_url: labelUrl }).eq('id', submissionId);
    if (updateError) {
      toast({ title: 'Error saving label URL', description: updateError.message, status: 'error', duration: 5000, isClosable: true });
      return;
    }
    setSubmissions(submissions.map(sub => sub.id === submissionId ? { ...sub, fedex_label_url: labelUrl } : sub));
    toast({ title: 'FedEx label uploaded', status: 'success', duration: 3000, isClosable: true });
  };

  const handleLabResultsUpload = async (submissionId, file) => {
    if (!file) return;
    const fileExt = file.name.split('.').pop();
    const filePath = `lab-results/${submissionId}.${fileExt}`;
    const { error: uploadError } = await supabase.storage.from('lab-results').upload(filePath, file, { upsert: true });
    if (uploadError) {
      toast({ title: 'Upload failed', description: uploadError.message, status: 'error', duration: 5000, isClosable: true });
      return;
    }
    const { data: urlData } = supabase.storage.from('lab-results').getPublicUrl(filePath);
    const resultsUrl = urlData.publicUrl;
    
    // Find the current submission to check its status
    const currentSubmission = submissions.find(sub => sub.id === submissionId);
    const shouldComplete = currentSubmission && currentSubmission.status !== 'completed';
    
    // Update both lab_results_url and status (if not already completed)
    const updateData = { lab_results_url: resultsUrl };
    if (shouldComplete) {
      updateData.status = 'completed';
    }
    
    const { error: updateError } = await supabase.from('submissions').update(updateData).eq('id', submissionId);
    if (updateError) {
      toast({ title: 'Error saving lab results URL', description: updateError.message, status: 'error', duration: 5000, isClosable: true });
      return;
    }
    
    // Update the submissions state with both lab results URL and status
    setSubmissions(submissions.map(sub => 
      sub.id === submissionId 
        ? { ...sub, lab_results_url: resultsUrl, ...(shouldComplete && { status: 'completed' }) }
        : sub
    ));
    
    if (shouldComplete) {
      toast({ title: 'Lab results uploaded - Submission automatically marked as completed', status: 'success', duration: 3000, isClosable: true });
    } else {
      toast({ title: 'Lab results uploaded', status: 'success', duration: 3000, isClosable: true });
    }

    // Notify the doctor via email if doctor_email exists
    if (currentSubmission?.doctor_email) {
      const apiBase = process.env.REACT_APP_API_URL || '';
      try {
        await fetch(`${apiBase}/api/notify-doctor-results`, {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify({
            submissionId,
            doctorEmail: currentSubmission.doctor_email,
            doctorName: currentSubmission.doctor_name,
            patientName: currentSubmission.patient_name,
            resultsUrl,
          }),
        });
      } catch (notifyErr) {
        console.error('Failed to notify doctor:', notifyErr);
      }
    }
  };

  // FedEx label generation functions
  const handleGenerateFedexLabel = (submission) => {
    setSelectedSubmissionForFedex(submission);
    setFedexModalOpen(true);
  };

  const handleFedexModalClose = () => {
    setFedexModalOpen(false);
    setSelectedSubmissionForFedex(null);
  };

  const handleFedexLabelGenerated = (labelData) => {
    // Update the submission with the new label information
    setSubmissions(prev => prev.map(sub => 
      sub.id === selectedSubmissionForFedex.id 
        ? { ...sub, fedex_label_url: labelData.labelUrl, fedex_tracking_number: labelData.trackingNumber }
        : sub
    ));
    
    toast({
      title: 'FedEx Label Generated',
      description: `Label created successfully. Tracking: ${labelData.trackingNumber}`,
      status: 'success',
      duration: 5000,
      isClosable: true
    });
    
    handleFedexModalClose();
  };

  const getStatusColor = (status) => getSubmissionStatusColor(status);

  const getLabName = (lab_id, submission = null) => {
    // First, try to get lab name from the joined submission data (most reliable)
    if (submission && submission.labs) {
      // If labs is an array (Supabase returns as array for one-to-many, but should be object for one-to-one)
      if (Array.isArray(submission.labs) && submission.labs.length > 0) {
        return submission.labs[0].name;
      }
      // If labs is an object (Supabase sometimes returns as object)
      if (submission.labs && submission.labs.name) {
        return submission.labs.name;
      }
    }
    
    if (!lab_id) return 'Unknown';
    
    // Try the map (fastest)
    if (labsMap[lab_id]) return labsMap[lab_id].name;
    
    const labIdStr = String(lab_id);
    if (labsMap[labIdStr]) return labsMap[labIdStr].name;
    
    // Try lowercase
    if (labsMap[labIdStr.toLowerCase()]) return labsMap[labIdStr.toLowerCase()].name;
    
    // Fallback to array search (for backward compatibility)
    if (labs && labs.length > 0) {
      let lab = labs.find(l => l.id === lab_id);
      if (lab) return lab.name;
      
      lab = labs.find(l => String(l.id) === labIdStr);
      if (lab) return lab.name;
      
      const labIdLower = labIdStr.trim().toLowerCase();
      lab = labs.find(l => String(l.id).trim().toLowerCase() === labIdLower);
      if (lab) return lab.name;
    }
    
    return 'Unknown';
  };

  const filteredSubmissions = useMemo(() => {
    const filtered = submissions.filter(submission => {
      // Optional filter by specific phlebotomist (when coming from Phlebotomist stats)
      if (phlebotomistIdFilter && submission.phlebotomist_id !== phlebotomistIdFilter) {
        return false;
      }
      // When viewing a single phleb, hide their done draws unless "Show
      // completed" is on — keeps the focus on what still needs doing.
      if (phlebotomistIdFilter && !showCompleted && !isOpenDraw(submission)) {
        return false;
      }

      // Status filter — default visible for any status NOT explicitly in statusFilters,
      // so newly-introduced status codes don't silently disappear from the list. Only
      // hide when the status is in the filter object AND explicitly toggled off.
      {
        const key = submission.status?.toLowerCase() || 'pending';
        if (Object.prototype.hasOwnProperty.call(statusFilters, key) && !statusFilters[key]) {
          return false;
        }
      }

      // Payment filter
      if (paymentFilter === 'self_pay_unpaid' && !(submission.is_self_pay && submission.payment_status !== 'paid')) return false;
      if (paymentFilter === 'self_pay_paid' && !(submission.is_self_pay && submission.payment_status === 'paid')) return false;
      if (paymentFilter === 'insurance' && submission.is_self_pay) return false;

      // Overdue-update view: only draws past their next-update SLA
      if (overdueOnly && getNextUpdateInfo(submission).state !== 'overdue') return false;

      // Date range, inclusive. Applies either to the draw date (submitted_at)
      // or the scheduled appointment date, depending on dateField.
      if (dateFrom || dateTo) {
        if (dateField === 'scheduled') {
          // A scheduled draw can be an exact date or a window [start,end];
          // include it when that range overlaps the selected range. Compared as
          // "YYYY-MM-DD" strings (lexicographic = chronological) so exact times
          // and windows both match without timezone drift. Draws with no
          // schedule are hidden when filtering by scheduled date.
          const range = getScheduledRange(submission);
          if (!range) return false;
          if (dateFrom && range.end < dateFrom) return false;
          if (dateTo && range.start > dateTo) return false;
        } else {
          const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
          const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;
          const raw = submission.submitted_at || submission.created_at;
          if (!raw) return false;
          const d = new Date(raw);
          if (isNaN(d)) return false;
          if (from && d < from) return false;
          if (to && d > to) return false;
        }
      }

      // Tag filter
      if (tagFilter && !(Array.isArray(submission.tags) && submission.tags.includes(tagFilter))) return false;

      // Client code filter (the practice's lab account code from LDX)
      if (clientCodeFilter && String(submission.client_code || '') !== clientCodeFilter) return false;

      // "Our Clients" toggle — only submissions from our own practices
      if (ourClientsOnly && !OUR_CLIENT_CODES.has(String(submission.client_code || ''))) return false;

      if (searchQuery.trim()) {
        const searchLower = searchQuery.trim().toLowerCase();
        const joinedLabName = submission.labs?.name;
        const labName = joinedLabName || getLabName(submission.lab_id, submission);
        const fullAddress = [
          submission.patient_address,
          submission.patient_street,
          submission.patient_city,
          submission.patient_state,
          submission.patient_zip,
        ].filter(Boolean).join(' ').toLowerCase();
        return (
          submission.patient_name?.toLowerCase().includes(searchLower) ||
          submission.doctor_name?.toLowerCase().includes(searchLower) ||
          submission.status?.toLowerCase().includes(searchLower) ||
          submission.phlebotomist_name?.toLowerCase().includes(searchLower) ||
          submission.phlebotomist_profiles?.company_name?.toLowerCase().includes(searchLower) ||
          submission.client_name?.toLowerCase().includes(searchLower) ||
          submission.client_code?.toLowerCase().includes(searchLower) ||
          submission.patient_zip?.toLowerCase().includes(searchLower) ||
          fullAddress.includes(searchLower) ||
          labName?.toLowerCase().includes(searchLower)
        );
      }
      
      return true;
    });
    
    const byDateDesc = (a, b) => {
      const aDate = a.submitted_at || a.created_at;
      const bDate = b.submitted_at || b.created_at;
      if (bDate && aDate) return new Date(bDate) - new Date(aDate);
      return 0;
    };

    if (sortMode === 'priority') {
      // Priority view: new patients (never updated) first, then callbacks by
      // urgency (most overdue → soonest due), then everything else. Within each
      // bucket, newest first.
      const rank = (s) => {
        const st = getNextUpdateInfo(s).state;
        if (st === 'none') return 0;     // brand-new, needs first contact
        if (st === 'overdue') return 1;  // callback overdue
        if (st === 'due') return 2;      // callback upcoming
        return 3;                        // done / completed
      };
      return filtered.sort((a, b) => {
        const ra = rank(a), rb = rank(b);
        if (ra !== rb) return ra - rb;
        // Within the overdue/due buckets, sort by time-until-due ascending
        // (most overdue first, then soonest due).
        if (ra === 1 || ra === 2) {
          const da = getNextUpdateInfo(a).dueMs;
          const db = getNextUpdateInfo(b).dueMs;
          if (da != null && db != null) return da - db;
        }
        return byDateDesc(a, b);
      });
    }

    // Most recent: newest first.
    return filtered.sort(byDateDesc);
  }, [submissions, searchQuery, statusFilters, paymentFilter, phlebotomistIdFilter, overdueOnly, showCompleted, sortMode, dateFrom, dateTo, dateField, tagFilter, clientCodeFilter, ourClientsOnly, labs, labsMap]);

  // Distinct tags across all submissions, with counts, for the tag-filter chips.
  const availableTags = useMemo(() => {
    const counts = {};
    submissions.forEach((s) => {
      if (Array.isArray(s.tags)) s.tags.forEach((t) => { if (t) counts[t] = (counts[t] || 0) + 1; });
    });
    return Object.entries(counts).map(([tag, count]) => ({ tag, count })).sort((a, b) => b.count - a.count);
  }, [submissions]);

  // Distinct practice client codes (LDX sending facility), with counts and the
  // doctors seen under each so the filter reads "5435 — Gerald Williams".
  const availableClientCodes = useMemo(() => {
    const byCode = new Map();
    submissions.forEach((s) => {
      const code = s.client_code && String(s.client_code).trim();
      if (!code) return;
      if (!byCode.has(code)) byCode.set(code, { count: 0, doctors: new Set() });
      const entry = byCode.get(code);
      entry.count += 1;
      if (s.doctor_name) entry.doctors.add(String(s.doctor_name).trim());
    });
    return Array.from(byCode.entries())
      .map(([code, { count, doctors }]) => {
        const names = Array.from(doctors).sort((a, b) => a.localeCompare(b));
        const label = names.length ? `${code} — ${names.slice(0, 2).join(', ')}${names.length > 2 ? '…' : ''}` : code;
        return { code, label, count };
      })
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
  }, [submissions]);

  // Detect duplicate draws: same patient + same draw date. Keyed on a patient
  // identity (name, falling back to DOB/MRN) plus the YYYY-MM-DD of the draw.
  // Any key with 2+ rows flags all of them so they can be highlighted. Computed
  // across ALL submissions (not just the filtered page) so duplicates surface
  // even when split across pages. Cancelled rows are ignored.
  const duplicateIds = useMemo(() => {
    const groups = {};
    submissions.forEach((s) => {
      if ((s.status || '').toLowerCase() === 'cancelled') return;
      const name = (s.patient_name || '').trim().toLowerCase();
      const ident = name || s.patient_dob || s.patient_mrn;
      const raw = s.submitted_at || s.created_at;
      if (!ident || !raw) return;
      const day = new Date(raw);
      if (isNaN(day)) return;
      const key = `${ident}|${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`;
      (groups[key] = groups[key] || []).push(s.id);
    });
    const dup = new Set();
    Object.values(groups).forEach((ids) => {
      if (ids.length > 1) ids.forEach((id) => dup.add(id));
    });
    return dup;
  }, [submissions]);

  // Build a plain-text digest of exactly what the current filter view shows —
  // totals, per-status/outcome/phleb/lab/client tallies, duplicates, and a
  // detailed line per patient — and copy it to the clipboard.
  const handleCopySummary = async () => {
    const list = filteredSubmissions;
    const pct = (n) => (list.length ? ` (${Math.round((n / list.length) * 100)}%)` : '');

    // Describe the active filters so the reader knows what "shown" means.
    const filtersDesc = [];
    if (searchQuery.trim()) filtersDesc.push(`search "${searchQuery.trim()}"`);
    const hidden = Object.entries(statusFilters).filter(([, v]) => !v).map(([k]) => getSubmissionStatusLabel(k));
    if (hidden.length) filtersDesc.push(`hiding statuses: ${hidden.join(', ')}`);
    if (paymentFilter !== 'all') filtersDesc.push(`payment: ${paymentFilter.replace(/_/g, ' ')}`);
    if (overdueOnly) filtersDesc.push('overdue updates only');
    if (dateFrom || dateTo) filtersDesc.push(`${dateField === 'scheduled' ? 'scheduled' : 'draw'} date ${dateFrom || 'any'} → ${dateTo || 'any'}`);
    if (tagFilter) filtersDesc.push(`tag: ${tagFilter}`);
    if (clientCodeFilter) filtersDesc.push(`client code: ${clientCodeFilter}`);
    if (ourClientsOnly) filtersDesc.push('Our Clients only');
    if (phlebotomistIdFilter) {
      const p = phlebotomists[phlebotomistIdFilter];
      filtersDesc.push(`phlebotomist: ${p?.full_name || p?.company_name || phlebotomistIdFilter}`);
    }

    const tally = (keyOf) => {
      const m = new Map();
      list.forEach((s) => { const k = keyOf(s); m.set(k, (m.get(k) || 0) + 1); });
      return Array.from(m.entries()).sort((a, b) => b[1] - a[1]);
    };
    const byStatus = tally((s) => getSubmissionStatusLabel(s.status || 'pending'));
    const byOutcome = tally((s) => (s.outcome_code ? (getOutcomeConfig(s.outcome_code)?.label || s.outcome_code) : 'No outcome logged'));
    const byLab = tally((s) => (s.lab_id ? getLabName(s.lab_id, s) : 'No lab assigned'));
    const byClient = tally((s) => (s.client_code ? `Client ${s.client_code}` : 'No client code'));
    const dups = list.filter((s) => duplicateIds.has(s.id));
    const overdue = list.filter((s) => getNextUpdateInfo(s).state === 'overdue');
    const selfPayUnpaid = list.filter((s) => s.is_self_pay && s.payment_status !== 'paid');

    const L = [];
    L.push(`BLOOD FILES SUMMARY — ${new Date().toLocaleString('en-US', { dateStyle: 'full', timeStyle: 'short' })}`);
    L.push(filtersDesc.length ? `Filters: ${filtersDesc.join('; ')}` : 'Filters: none (showing everything)');
    L.push('');
    L.push(`TOTAL SHOWN: ${list.length} patient${list.length === 1 ? '' : 's'}`);
    L.push('');
    L.push('BY STATUS:');
    byStatus.forEach(([k, n]) => L.push(`  - ${k}: ${n}${pct(n)}`));
    L.push('');
    L.push('BY LATEST OUTCOME:');
    byOutcome.forEach(([k, n]) => L.push(`  - ${k}: ${n}${pct(n)}`));
    L.push('');
    L.push(`POSSIBLE DUPLICATES (same patient, same day): ${dups.length}${dups.length ? ` — ${[...new Set(dups.map((s) => s.patient_name || 'Unknown'))].join(', ')}` : ''}`);
    L.push(`OVERDUE FOR UPDATE: ${overdue.length}`);
    L.push(`SELF-PAY, NOT YET PAID: ${selfPayUnpaid.length}`);
    L.push('');
    L.push('BY LAB:');
    byLab.forEach(([k, n]) => L.push(`  - ${k}: ${n}${pct(n)}`));
    L.push('');
    L.push('BY CLIENT CODE:');
    byClient.forEach(([k, n]) => L.push(`  - ${k}: ${n}${pct(n)}`));

    const text = L.join('\n');
    const copied = () => toast({ title: `Summary of ${list.length} patient${list.length === 1 ? '' : 's'} copied`, description: 'Paste it anywhere as plain text.', status: 'success', duration: 3000, isClosable: true });
    try {
      await navigator.clipboard.writeText(text);
      copied();
    } catch (err) {
      // Clipboard API needs a secure context / permission — fall back to the
      // hidden-textarea trick before giving up.
      try {
        const ta = document.createElement('textarea');
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        copied();
      } catch {
        toast({ title: 'Could not copy to clipboard', description: err.message, status: 'error', duration: 4000, isClosable: true });
      }
    }
  };

  // Submissions scoped to the date range only (not the status/search/phleb
  // filters) — the dashboard stats count these so they reflect the chosen
  // window without the per-chip filters making the counts circular.
  // Dashboard stats describe the same set the list shows (filteredSubmissions),
  // so the "patients" total always matches the visible list — including the
  // status filters (e.g. cancelled draws are hidden from both).

  // Phlebotomists that still have OPEN draws — for the filter chips. A draw is
  // "done" (and ignored here) once it's drawn, in the lab, completed, or
  // cancelled, so a phleb whose draws are all done won't show a chip. Each
  // entry: { id, name, count of open draws }, sorted by most open first.
  const phlebsWithDraws = useMemo(() => {
    const map = {};
    submissions.forEach((s) => {
      if (!s.phlebotomist_id) return;
      if (!isOpenDraw(s)) return;
      const id = s.phlebotomist_id;
      if (!map[id]) {
        const name =
          s.phlebotomist_name ||
          phlebotomists[id]?.full_name ||
          phlebotomists[id]?.company_name ||
          'Phlebotomist';
        map[id] = { id, name, count: 0 };
      }
      map[id].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [submissions, phlebotomists]);

  // Reset to page 1 when filters or search change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilters, paymentFilter, phlebotomistIdFilter, overdueOnly, showCompleted, dateFrom, dateTo, dateField, tagFilter, clientCodeFilter, ourClientsOnly]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredSubmissions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSubmissions = filteredSubmissions.slice(startIndex, endIndex);

  // Compute completion progress and missing sections for a submission
  const computeCompletion = (submission) => {
    const sections = {
      Patient: ['patient_name', 'patient_email', 'patient_dob', 'patient_address', 'patient_gender'],
      Doctor: ['doctor_name', 'doctor_phone', 'doctor_email', 'doctor_address'],
      Lab: ['lab_id', 'blood_collection_time'],
      Insurance: ['insurance_id', 'policy_number'],
      Documents: ['script_image', 'insurance_card_image', 'patient_id_image'],
      Phlebotomist: ['phlebotomist_name', 'phlebotomist_email'],
    };

    const allFields = Object.values(sections).flat();
    const isFilled = (field) => {
      const value = submission[field];
      if (['script_image', 'insurance_card_image', 'patient_id_image'].includes(field)) {
        return Array.isArray(value) && value.filter(Boolean).length > 0;
      }
      return value && value.toString().trim() !== '';
    };

    const filledCount = allFields.filter(isFilled).length;
    const totalCount = allFields.length;
    const percent = Math.round((filledCount / Math.max(1, totalCount)) * 100);

    // First missing section name (if any)
    const firstMissingSection = Object.entries(sections).find(([sectionName, fields]) =>
      fields.some((f) => !isFilled(f))
    )?.[0];

    return { percent, filledCount, totalCount, firstMissingSection };
  };

  const isReadyForFedex = (submission) => {
    const requiredFields = [
      // Patient
      'patient_name', 'patient_email', 'patient_dob', 'patient_address',
      // Doctor
      'doctor_name', 'doctor_phone', 'doctor_email', 'doctor_address',
      // Insurance
      'insurance_id', 'policy_number',
      // Documents
      'script_image'
    ];
    const hasValue = (field) => {
      const value = submission[field];
      if (field === 'script_image') {
        return Array.isArray(value) && value.filter(Boolean).length > 0;
      }
      return !!(value && value.toString().trim() !== '');
    };
    return requiredFields.every(hasValue);
  };

  const waitingForFedex = submissions.filter(sub => sub.need_fedex_label && !sub.fedex_label_url);

  const validSubmissions = submissions.filter(sub => sub.phlebotomist_name && sub.phlebotomist_name !== 'Unknown' && sub.phlebotomist_name.trim() !== '');

  const handleLabChange = (submission) => {
    setSelectedSubmission(submission);
    setSelectedLabId(submission.lab_id || '');
    onOpen();
  };

  // ── Phleb assignment ────────────────────────────────────────────────────
  // Same shape as handleLabChange/handleUpdateLab. Sending '' clears the
  // assignment, which is useful when a draw was wrongly claimed.
  const handlePhlebChange = (submission) => {
    setSelectedSubmission(submission);
    setSelectedPhlebotomistId(submission.phlebotomist_id || '');
    onPhlebModalOpen();
  };

  const handleUpdatePhleb = async () => {
    if (!selectedSubmission) return;
    setUpdatingPhleb(true);
    try {
      const newId = selectedPhlebotomistId || null;
      const oldId = selectedSubmission.phlebotomist_id || null;
      // Persist the denormalized name + email alongside the id. Skipping these
      // used to leave the submission detail page showing "Unknown" (id set, name
      // null) and left invoices/pay without a phleb name.
      const newName = newId
        ? (phlebotomists[newId]?.full_name || phlebotomists[newId]?.company_name || null)
        : null;
      const newEmail = newId ? (phlebotomists[newId]?.email || null) : null;
      const { error } = await supabase
        .from('submissions')
        .update({ phlebotomist_id: newId, phlebotomist_name: newName, phlebotomist_email: newEmail })
        .eq('id', selectedSubmission.id);
      if (error) throw error;

      // Record the assign/unassign on the timeline, but only when it actually
      // changed (opening the modal and saving the same phleb shouldn't log).
      if (oldId !== newId) {
        const oldName = selectedSubmission.phlebotomist_name
          || (oldId ? (phlebotomists[oldId]?.full_name || phlebotomists[oldId]?.company_name || 'a phlebotomist') : null);
        logAssignmentChange({
          submissionId: selectedSubmission.id,
          fromName: oldName,
          toName: newName,
          actorKind: 'admin',
        });
      }

      setSubmissions(submissions.map(sub =>
        sub.id === selectedSubmission.id
          ? { ...sub, phlebotomist_id: newId, phlebotomist_name: newName ?? sub.phlebotomist_name }
          : sub
      ));

      toast({
        title: newId ? 'Phlebotomist assigned' : 'Phlebotomist cleared',
        description: newName ? `Now assigned to ${newName}` : undefined,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onPhlebModalClose();
    } catch (error) {
      toast({
        title: 'Error updating phlebotomist',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingPhleb(false);
    }
  };

  const handleUpdateLab = async () => {
    if (!selectedSubmission || !selectedLabId) return;

    setUpdatingLab(true);
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ lab_id: selectedLabId })
        .eq('id', selectedSubmission.id);

      if (error) throw error;

      setSubmissions(submissions.map(sub =>
        sub.id === selectedSubmission.id ? { ...sub, lab_id: selectedLabId } : sub
      ));

      toast({
        title: 'Lab updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      toast({
        title: 'Error updating lab',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdatingLab(false);
    }
  };

  const handleDeleteClick = (submission, e) => {
    e.stopPropagation();
    setSubmissionToDelete(submission);
    onDeleteDialogOpen();
  };

  const handleConfirmDelete = async () => {
    if (!submissionToDelete) return;
    setDeleteLoading(true);
    try {
      const { error } = await supabase
        .from('submissions')
        .update({ status: 'cancelled' })
        .eq('id', submissionToDelete.id);

      if (error) throw error;

      setSubmissions(prev =>
        prev.map(s => (s.id === submissionToDelete.id ? { ...s, status: 'cancelled' } : s))
      );

      toast({
        title: 'Submission cancelled',
        description: `"${submissionToDelete.patient_name || 'Submission'}" has been marked as cancelled.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onDeleteDialogClose();
      setSubmissionToDelete(null);
    } catch (error) {
      toast({
        title: 'Error cancelling submission',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleteLoading(false);
    }
  };

  const getFileCount = (submission) => {
    let count = 0;
    if (Array.isArray(submission.script_image)) count += submission.script_image.filter(Boolean).length;
    if (Array.isArray(submission.insurance_card_image)) count += submission.insurance_card_image.filter(Boolean).length;
    if (Array.isArray(submission.patient_id_image)) count += submission.patient_id_image.filter(Boolean).length;
    return count;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditingForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleFileUpload = async (field, file) => {
    if (!file || !selectedSubmission) return;
    const fileExt = file.name.split('.').pop();
    const filePath = `${field}/${selectedSubmission.id}.${fileExt}`;
    const { error: uploadError } = await supabase.storage.from(field).upload(filePath, file, { upsert: true });
    if (uploadError) {
      toast({ title: 'Upload failed', description: uploadError.message, status: 'error' });
      return;
    }
    const { data: urlData } = supabase.storage.from(field).getPublicUrl(filePath);
    let column = '';
    if (field === 'lab-results') column = 'lab_results_url';
    else if (field === 'fedex-labels') column = 'fedex_label_url';
    else column = `${field}_url`;
    
    // Update the editing form
    setEditingForm(f => ({ ...f, [column]: urlData.publicUrl }));
    
    // Auto-complete submission if lab results are uploaded (unless already completed)
    if (field === 'lab-results' && selectedSubmission.status !== 'completed') {
      setEditingForm(f => ({ ...f, status: 'completed' }));
      toast({ title: 'Lab results uploaded - Submission automatically marked as completed', status: 'success' });
    } else {
      toast({ title: 'File uploaded', status: 'success' });
    }
  };

  const handleSave = async () => {
    if (!selectedSubmission) return;
    setSaving(true);
    const { error } = await supabase.from('submissions').update(editingForm).eq('id', selectedSubmission.id);
    setSaving(false);
    if (error) {
      toast({ title: 'Error saving', description: error.message, status: 'error' });
    } else {
      toast({ title: 'Saved successfully', status: 'success' });
      setSubmissions(submissions.map(sub => 
        sub.id === selectedSubmission.id ? { ...sub, ...editingForm } : sub
      ));
      setSelectedSubmission({ ...selectedSubmission, ...editingForm });
    }
  };

  useEffect(() => {
    if (selectedSubmission) {
      setEditingForm(selectedSubmission);
    }
  }, [selectedSubmission]);

  const toggleStatusFilter = (status) => {
    setStatusFilters(prev => {
      const isOnlyOneActive = Object.values(prev).filter(Boolean).length === 1 && prev[status];
      // If this is the only active filter and user clicks it again → show all (except cancelled)
      if (isOnlyOneActive) {
        return {
          pending: true,
          in_progress: true,
          completed: true,
          cancelled: false
        };
      }
      // Otherwise → show only the clicked status
      return {
        pending: false,
        in_progress: false,
        completed: false,
        cancelled: false,
        [status]: true
      };
    });
  };

  const showAllStatuses = () => {
    setStatusFilters({
      pending: true,
      in_progress: true,
      completed: true,
      cancelled: false
    });
  };

  const openPreview = (url) => { setPreviewUrl(url); setIsPreviewOpen(true); };
  const closePreview = () => { setIsPreviewOpen(false); setPreviewUrl(null); };

  const SubmissionDetails = ({ submission }) => {
    // State for editing each section - must be at top level
    const [editingSection, setEditingSection] = useState(null);
    const [editForm, setEditForm] = useState({});
    const [doctors, setDoctors] = useState([]);
    const [doctorsLoading, setDoctorsLoading] = useState(false);
    const [isNewDoctor, setIsNewDoctor] = useState(false);
    const [newDoctorForm, setNewDoctorForm] = useState({
      full_name: '',
      email: '',
      phone: '',
      clinic_name: '',
      address: ''
    });

    if (!submission) return <Box p={8} textAlign="center">Select a submission to view details</Box>;

    // Calculate completion percentage and missing fields
    const calculateCompletion = () => {
      const requiredFields = [
        'patient_name', 'patient_email', 'patient_dob', 'patient_address', 'patient_gender',
        'doctor_name', 'doctor_phone', 'doctor_email', 'doctor_address',
        'lab_id', 'blood_collection_time',
        'insurance_id', 'policy_number',
        'phlebotomist_name', 'phlebotomist_email'
      ];
      
      const documentFields = [
        'script_image', 'insurance_card_image', 'patient_id_image'
      ];
      
      const allFields = [...requiredFields, ...documentFields];
      const filledFields = allFields.filter(field => {
        const value = submission[field];
        if (documentFields.includes(field)) {
          return value && Array.isArray(value) && value.length > 0;
        }
        return value && value.toString().trim() !== '';
      });
      
      const completionPercentage = Math.round((filledFields.length / allFields.length) * 100);
      const missingFields = allFields.filter(field => {
        const value = submission[field];
        if (documentFields.includes(field)) {
          return !value || !Array.isArray(value) || value.length === 0;
        }
        return !value || value.toString().trim() === '';
      });
      
      return { completionPercentage, missingFields, filledFields };
    };

    const { completionPercentage, missingFields } = calculateCompletion();

    const getStatusColor = (status) => getSubmissionStatusColor(status);

    const fetchDoctors = async () => {
      setDoctorsLoading(true);
      try {
        const { data, error } = await supabase
          .from('doctors')
          .select('id, full_name, email, phone, clinic_name, address')
          .order('full_name');
        
        if (error) {
          console.error('Error fetching doctors:', error);
        } else {
          setDoctors(data || []);
        }
      } catch (error) {
        console.error('Error fetching doctors:', error);
      } finally {
        setDoctorsLoading(false);
      }
    };

    const handleAddNewDoctor = async () => {
      try {
        const { data: newDoctor, error } = await supabase
          .from('doctors')
          .insert(newDoctorForm)
          .select()
          .single();

        if (error) throw error;

        // Add the new doctor to the local list
        setDoctors([...doctors, newDoctor]);

        // Auto-select the new doctor
        setEditForm({
          ...editForm,
          doctor_name: newDoctor.full_name,
          doctor_phone: newDoctor.phone,
          doctor_email: newDoctor.email,
          doctor_address: newDoctor.address,
          client_name: newDoctor.clinic_name || editForm.client_name || '',
          selectedDoctorId: newDoctor.id
        });

        // Reset the new doctor form
        setNewDoctorForm({
          full_name: '',
          email: '',
          phone: '',
          clinic_name: '',
          address: ''
        });

        setIsNewDoctor(false);

        toast({
          title: 'New doctor added successfully',
          description: `${newDoctor.full_name} has been added to the database`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error adding new doctor:', error);
        toast({
          title: 'Error adding new doctor',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    const handleEditSection = (section) => {
      setEditingSection(section);
      setIsNewDoctor(false);
      
      // Fetch doctors when editing doctor section
      if (section === 'doctor') {
        fetchDoctors();
      }
      
      // Initialize edit form with current values
      const sectionFields = {
        patient: ['patient_name', 'patient_email', 'patient_dob', 'patient_address'],
        doctor: ['doctor_name', 'doctor_phone', 'doctor_email', 'doctor_address', 'client_name'],
        lab: ['lab_brand', 'blood_collection_time'],
        insurance: ['insurance_company', 'insurance_policy_number'],
        phlebotomist: ['phlebotomist_name', 'phlebotomist_email']
      };
      
      const fields = sectionFields[section] || [];
      const initialValues = {};
      fields.forEach(field => {
        initialValues[field] = submission[field] || '';
      });
      setEditForm(initialValues);
    };

    const handleSaveSection = async () => {
      try {
        let updateData = { ...editForm };
        
        // Remove the selectedDoctorId from update data as it's not a submission field
        delete updateData.selectedDoctorId;

        const { error } = await supabase
          .from('submissions')
          .update(updateData)
          .eq('id', submission.id);

        if (error) throw error;

        // Update local submission data immutably
        setSubmissions(prev => prev.map(sub =>
          sub.id === submission.id ? { ...sub, ...updateData } : sub
        ));

        setEditingSection(null);
        setEditForm({});
        setIsNewDoctor(false);
        toast({
          title: 'Section updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } catch (error) {
        console.error('Error updating section:', error);
        toast({
          title: 'Error updating section',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    };

    const handleCancelEdit = () => {
      setEditingSection(null);
      setEditForm({});
      setIsNewDoctor(false);
      setNewDoctorForm({
        full_name: '',
        email: '',
        phone: '',
        clinic_name: '',
        address: ''
      });
    };

    return (
      <Box p={6} bg="white" borderRadius="lg" boxShadow="sm">
        {/* NEW DESIGN: Header with Progress */}
        <VStack spacing={4} align="stretch" mb={6}>
          <HStack justify="space-between" align="center">
            <VStack align="start" spacing={1}>
              <Heading size="lg" color="gray.700">
                Submission #{submission.id}
              </Heading>
              <Text fontSize="sm" color="gray.500">
                {formatPatientName(submission.patient_name)} • {submission.doctor_name}
              </Text>
            </VStack>
            <Badge 
              colorScheme={getStatusColor(submission.status)} 
              fontSize="sm" 
              px={3} 
              py={1} 
              borderRadius="full"
              textTransform="capitalize"
            >
              {(submission.status || '').replace(/_/g, ' ')}
            </Badge>
          </HStack>
          
          {/* Progress Bar */}
          <Box>
            <HStack justify="space-between" mb={2}>
              <Text fontSize="sm" fontWeight="medium" color="gray.600">
                Completion Progress
              </Text>
              <Text fontSize="sm" fontWeight="bold" color="blue.600">
                {completionPercentage}%
              </Text>
            </HStack>
            <Progress 
              value={completionPercentage} 
              colorScheme="blue" 
              size="lg" 
              borderRadius="full"
              bg="gray.100"
            />
          </Box>

          {/* Missing Fields Alert */}
          {missingFields.length > 0 && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertTitle fontSize="sm">Missing Required Information!</AlertTitle>
                <AlertDescription fontSize="xs">
                  {missingFields.length} field(s) still need to be completed: {missingFields.slice(0, 3).join(', ')}
                  {missingFields.length > 3 && ` and ${missingFields.length - 3} more...`}
                </AlertDescription>
              </Box>
            </Alert>
          )}
        </VStack>

        {/* NEW DESIGN: Organized Sections */}
        <VStack spacing={6} align="stretch" mb={6}>
            {/* Patient Information */}
            <Card>
              <CardHeader bg="blue.50" borderTopRadius="md">
                <HStack justify="space-between">
                  <Heading size="md" color="blue.700">Patient Information</Heading>
                  <IconButton
                    icon={<EditIcon />}
                    size="sm"
                    colorScheme="blue"
                    variant="ghost"
                    onClick={() => handleEditSection('patient')}
                  />
                </HStack>
              </CardHeader>
              <CardBody>
                {editingSection === 'patient' ? (
                  <VStack spacing={4}>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                      <FormControl>
                        <FormLabel fontSize="sm">Name</FormLabel>
                        <Input
                          value={editForm.patient_name || ''}
                          onChange={(e) => setEditForm({...editForm, patient_name: e.target.value})}
                          size="sm"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Email</FormLabel>
                        <Input
                          value={editForm.patient_email || ''}
                          onChange={(e) => setEditForm({...editForm, patient_email: e.target.value})}
                          size="sm"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Date of Birth</FormLabel>
                        <Input
                          type="date"
                          value={editForm.patient_dob || ''}
                          onChange={(e) => setEditForm({...editForm, patient_dob: e.target.value})}
                          size="sm"
                        />
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Address</FormLabel>
                        <Textarea
                          value={editForm.patient_address || ''}
                          onChange={(e) => setEditForm({...editForm, patient_address: e.target.value})}
                          size="sm"
                          rows={2}
                        />
                      </FormControl>
                    </SimpleGrid>
                    <HStack>
                      <Button size="sm" colorScheme="green" onClick={handleSaveSection}>
                        Save
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                        Cancel
                      </Button>
                    </HStack>
                  </VStack>
                ) : (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Name</Text>
                      <Text fontSize="sm" color={submission.patient_name ? "gray.800" : "red.500"}>
                        {formatPatientName(submission.patient_name) || <Text as="span" fontStyle="italic">Missing</Text>}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Email</Text>
                      <Text fontSize="sm" color={submission.patient_email ? "gray.800" : "red.500"}>
                        {submission.patient_email || <Text as="span" fontStyle="italic">Missing</Text>}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Date of Birth</Text>
                      <Text fontSize="sm" color={submission.patient_dob ? "gray.800" : "red.500"}>
                        {submission.patient_dob || <Text as="span" fontStyle="italic">Missing</Text>}
                      </Text>
                    </Box>
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Address</Text>
                      <Text fontSize="sm" color={submission.patient_address ? "gray.800" : "red.500"}>
                        {submission.patient_address || <Text as="span" fontStyle="italic">Missing</Text>}
                      </Text>
                    </Box>
                  </SimpleGrid>
                )}
              </CardBody>
            </Card>

          {/* Doctor Information */}
          <Card>
            <CardHeader bg="green.50" borderTopRadius="md">
              <HStack justify="space-between">
                <Heading size="md" color="green.700">Doctor Information</Heading>
                <IconButton
                  icon={<EditIcon />}
                  size="sm"
                  colorScheme="green"
                  variant="ghost"
                  onClick={() => handleEditSection('doctor')}
                />
              </HStack>
            </CardHeader>
            <CardBody>
              {editingSection === 'doctor' ? (
                <VStack spacing={4}>
                  {/* Toggle between existing and new doctor */}
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant={!isNewDoctor ? "solid" : "outline"}
                      colorScheme="blue"
                      onClick={() => setIsNewDoctor(false)}
                    >
                      Select Existing Doctor
                    </Button>
                    <Button
                      size="sm"
                      variant={isNewDoctor ? "solid" : "outline"}
                      colorScheme="green"
                      onClick={() => setIsNewDoctor(true)}
                    >
                      Add New Doctor
                    </Button>
                  </HStack>

                  {!isNewDoctor ? (
                    // Existing Doctor Selection
                    <VStack spacing={4}>
                      <FormControl>
                        <FormLabel fontSize="sm">Select Doctor</FormLabel>
                        <HStack spacing={2}>
                          <Select
                            value={editForm.selectedDoctorId || ''}
                            onChange={(e) => {
                              const selectedDoctor = doctors.find(d => d.id === e.target.value);
                              if (selectedDoctor) {
                                setEditForm({
                                  ...editForm,
                                  doctor_name: selectedDoctor.full_name,
                                  doctor_phone: selectedDoctor.phone,
                                  doctor_email: selectedDoctor.email,
                                  doctor_address: selectedDoctor.address,
                                  client_name: selectedDoctor.clinic_name || editForm.client_name || '',
                                  selectedDoctorId: selectedDoctor.id
                                });
                              } else {
                                setEditForm({
                                  ...editForm,
                                  doctor_name: '',
                                  doctor_phone: '',
                                  doctor_email: '',
                                  doctor_address: '',
                                  client_name: editForm.client_name || '',
                                  selectedDoctorId: ''
                                });
                              }
                            }}
                            size="sm"
                            isDisabled={doctorsLoading}
                          >
                            <option value="">Select doctor from database...</option>
                            {doctors.map((doctor) => (
                              <option key={doctor.id} value={doctor.id}>
                                {doctor.full_name} - {doctor.clinic_name}
                              </option>
                            ))}
                          </Select>
                          {doctorsLoading && <Spinner size="sm" />}
                        </HStack>
                      </FormControl>
                      <FormControl>
                        <FormLabel fontSize="sm">Client Name</FormLabel>
                        <Input
                          value={editForm.client_name || ''}
                          onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })}
                          size="sm"
                          placeholder="Facility or client name"
                        />
                      </FormControl>

                      {/* Doctor Details Display (Read-only when selected) */}
                      {editForm.selectedDoctorId && (
                        <Box p={3} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                          <Text fontSize="sm" fontWeight="medium" color="green.700" mb={2}>
                            Selected Doctor Details:
                          </Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2}>
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.600">Name</Text>
                              <Text fontSize="sm" color="gray.800">{editForm.doctor_name}</Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.600">Client Name</Text>
                              <Text fontSize="sm" color="gray.800">{editForm.client_name || '—'}</Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.600">Phone</Text>
                              <Text fontSize="sm" color="gray.800">{editForm.doctor_phone}</Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.600">Email</Text>
                              <Text fontSize="sm" color="gray.800">{editForm.doctor_email}</Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" fontWeight="medium" color="gray.600">Address</Text>
                              <Text fontSize="sm" color="gray.800">{editForm.doctor_address}</Text>
                            </Box>
                          </SimpleGrid>
                        </Box>
                      )}
                    </VStack>
                  ) : (
                    // New Doctor Form
                    <VStack spacing={4}>
                      <Text fontSize="sm" fontWeight="medium" color="green.700">
                        Add New Doctor to Database
                      </Text>
                      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                        <FormControl>
                          <FormLabel fontSize="sm">Full Name *</FormLabel>
                          <Input
                            value={newDoctorForm.full_name}
                            onChange={(e) => setNewDoctorForm({...newDoctorForm, full_name: e.target.value})}
                            size="sm"
                            placeholder="Doctor's full name"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm">Clinic Name</FormLabel>
                          <Input
                            value={newDoctorForm.clinic_name}
                            onChange={(e) => setNewDoctorForm({...newDoctorForm, clinic_name: e.target.value})}
                            size="sm"
                            placeholder="Clinic or practice name"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm">Email</FormLabel>
                          <Input
                            value={newDoctorForm.email}
                            onChange={(e) => setNewDoctorForm({...newDoctorForm, email: e.target.value})}
                            size="sm"
                            placeholder="Email address"
                          />
                        </FormControl>
                        <FormControl>
                          <FormLabel fontSize="sm">Phone</FormLabel>
                          <Input
                            value={newDoctorForm.phone}
                            onChange={(e) => setNewDoctorForm({...newDoctorForm, phone: e.target.value})}
                            size="sm"
                            placeholder="Phone number"
                          />
                        </FormControl>
                        <FormControl gridColumn={{ base: "1", md: "1 / -1" }}>
                          <FormLabel fontSize="sm">Address</FormLabel>
                          <Textarea
                            value={newDoctorForm.address}
                            onChange={(e) => setNewDoctorForm({...newDoctorForm, address: e.target.value})}
                            size="sm"
                            rows={2}
                            placeholder="Clinic address"
                          />
                        </FormControl>
                      </SimpleGrid>
                    </VStack>
                  )}

                  {/* Action Buttons */}
                  <HStack>
                    {!isNewDoctor ? (
                      <Button 
                        size="sm" 
                        colorScheme="green" 
                        onClick={handleSaveSection}
                        isDisabled={!editForm.selectedDoctorId}
                      >
                        Save Doctor Selection
                      </Button>
                    ) : (
                      <Button 
                        size="sm" 
                        colorScheme="green" 
                        onClick={handleAddNewDoctor}
                        isDisabled={!newDoctorForm.full_name.trim()}
                      >
                        Add Doctor & Save
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Name</Text>
                    <Text fontSize="sm" color={submission.doctor_name ? "gray.800" : "red.500"}>
                      {submission.doctor_name || <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Phone</Text>
                    <Text fontSize="sm" color={submission.doctor_phone ? "gray.800" : "red.500"}>
                      {submission.doctor_phone || <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Email</Text>
                    <Text fontSize="sm" color={submission.doctor_email ? "gray.800" : "red.500"}>
                      {submission.doctor_email || <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Address</Text>
                    <Text fontSize="sm" color={submission.doctor_address ? "gray.800" : "red.500"}>
                      {submission.doctor_address || <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Client Name</Text>
                    <Text fontSize="sm" color={submission.client_name ? "gray.800" : "gray.500"}>
                      {submission.client_name || <Text as="span" fontStyle="italic">—</Text>}
                    </Text>
                  </Box>
                </SimpleGrid>
              )}
            </CardBody>
          </Card>

          {/* Documents */}
          <Card>
            <CardHeader bg="red.50" borderTopRadius="md">
              <HStack justify="space-between">
                <Heading size="md" color="red.700">Documents</Heading>
                <IconButton
                  icon={<EditIcon />}
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => handleEditSection('documents')}
                />
              </HStack>
            </CardHeader>
            <CardBody>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">Doctor Script</Text>
                  <Text fontSize="sm" color={submission.script_image && submission.script_image.length > 0 ? "green.600" : "red.500"}>
                    {submission.script_image && submission.script_image.length > 0 ? '✓ Uploaded' : 'Missing'}
                  </Text>
                  {submission.script_image && submission.script_image.length > 0 && (
                    <Button size="xs" mt={2} onClick={() => openPreview(submission.script_image[0])}>
                      View
                    </Button>
                  )}
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">Insurance Card</Text>
                  <Text fontSize="sm" color={submission.insurance_card_image && submission.insurance_card_image.length > 0 ? "green.600" : "red.500"}>
                    {submission.insurance_card_image && submission.insurance_card_image.length > 0 ? '✓ Uploaded' : 'Missing'}
                  </Text>
                  {submission.insurance_card_image && submission.insurance_card_image.length > 0 && (
                    <Button size="xs" mt={2} onClick={() => openPreview(submission.insurance_card_image[0])}>
                      View
                    </Button>
                  )}
                </Box>
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color="gray.600">Patient ID</Text>
                  <Text fontSize="sm" color={submission.patient_id_image && submission.patient_id_image.length > 0 ? "green.600" : "red.500"}>
                    {submission.patient_id_image && submission.patient_id_image.length > 0 ? '✓ Uploaded' : 'Missing'}
                  </Text>
                  {submission.patient_id_image && submission.patient_id_image.length > 0 && (
                    <Button size="xs" mt={2} onClick={() => openPreview(submission.patient_id_image[0])}>
                      View
                    </Button>
                  )}
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>
          {/* Lab Information */}
          <Card>
            <CardHeader bg="purple.50" borderTopRadius="md">
              <HStack justify="space-between">
                <Heading size="md" color="purple.700">Lab Information</Heading>
                <IconButton
                  icon={<EditIcon />}
                  size="sm"
                  colorScheme="purple"
                  variant="ghost"
                  onClick={() => handleEditSection('lab')}
                />
              </HStack>
            </CardHeader>
            <CardBody>
              {editingSection === 'lab' ? (
                <VStack spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                    <FormControl>
                      <FormLabel fontSize="sm">Lab Brand</FormLabel>
                      <Input
                        value={editForm.lab_brand || ''}
                        onChange={(e) => setEditForm({...editForm, lab_brand: e.target.value})}
                        size="sm"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Blood Collection Time</FormLabel>
                      <Input
                        type="datetime-local"
                        value={editForm.blood_collection_time || ''}
                        onChange={(e) => setEditForm({...editForm, blood_collection_time: e.target.value})}
                        size="sm"
                      />
                    </FormControl>
                  </SimpleGrid>
                  <HStack>
                    <Button size="sm" colorScheme="green" onClick={handleSaveSection}>
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Lab</Text>
                    <Text fontSize="sm" color={submission.lab_id ? "gray.800" : "red.500"}>
                      {submission.lab_id ? getLabName(submission.lab_id, submission) : <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Blood Collection Time</Text>
                    <Text fontSize="sm" color={submission.blood_collection_time ? "gray.800" : "red.500"}>
                      {submission.blood_collection_time || <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                  {submission.specimen_type && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Specimen</Text>
                      {(() => {
                        try {
                          const specs = JSON.parse(submission.specimen_type);
                          return Array.isArray(specs) ? specs.map((s, i) => <Text key={i} fontSize="sm">{s.quantity}x {s.type}</Text>) : <Text fontSize="sm">{submission.specimen_type}</Text>;
                        } catch { return <Text fontSize="sm">{submission.specimen_type}</Text>; }
                      })()}
                    </Box>
                  )}
                  {submission.diagnosis && (
                    <Box>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600">Diagnosis</Text>
                      <Text fontSize="sm">{submission.diagnosis}</Text>
                    </Box>
                  )}
                </SimpleGrid>
              )}
            </CardBody>
          </Card>

          {/* Insurance Information */}
          <Card>
            <CardHeader bg="orange.50" borderTopRadius="md">
              <HStack justify="space-between">
                <Heading size="md" color="orange.700">Insurance Information</Heading>
                <IconButton
                  icon={<EditIcon />}
                  size="sm"
                  colorScheme="orange"
                  variant="ghost"
                  onClick={() => handleEditSection('insurance')}
                />
              </HStack>
            </CardHeader>
            <CardBody>
              {editingSection === 'insurance' ? (
                <VStack spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                    <FormControl>
                      <FormLabel fontSize="sm">Insurance Company</FormLabel>
                      <Input
                        value={editForm.insurance_company || ''}
                        onChange={(e) => setEditForm({...editForm, insurance_company: e.target.value})}
                        size="sm"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Policy Number</FormLabel>
                      <Input
                        value={editForm.insurance_policy_number || ''}
                        onChange={(e) => setEditForm({...editForm, insurance_policy_number: e.target.value})}
                        size="sm"
                      />
                    </FormControl>
                  </SimpleGrid>
                  <HStack>
                    <Button size="sm" colorScheme="green" onClick={handleSaveSection}>
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Insurance</Text>
                    <Text fontSize="sm" color={submission.insurance_id ? "gray.800" : "red.500"}>
                      {submission.insurance_id || <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Policy Number</Text>
                    <Text fontSize="sm" color={submission.policy_number ? "gray.800" : "red.500"}>
                      {submission.policy_number || <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                </SimpleGrid>
              )}
            </CardBody>
          </Card>

          {/* Phlebotomist Information */}
          <Card>
            <CardHeader bg="teal.50" borderTopRadius="md">
              <HStack justify="space-between">
                <Heading size="md" color="teal.700">Phlebotomist Information</Heading>
                <IconButton
                  icon={<EditIcon />}
                  size="sm"
                  colorScheme="teal"
                  variant="ghost"
                  onClick={() => handleEditSection('phlebotomist')}
                />
              </HStack>
            </CardHeader>
            <CardBody>
              {editingSection === 'phlebotomist' ? (
                <VStack spacing={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} w="full">
                    <FormControl>
                      <FormLabel fontSize="sm">Name</FormLabel>
                      <Input
                        value={editForm.phlebotomist_name || ''}
                        onChange={(e) => setEditForm({...editForm, phlebotomist_name: e.target.value})}
                        size="sm"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel fontSize="sm">Email</FormLabel>
                      <Input
                        value={editForm.phlebotomist_email || ''}
                        onChange={(e) => setEditForm({...editForm, phlebotomist_email: e.target.value})}
                        size="sm"
                      />
                    </FormControl>
                  </SimpleGrid>
                  <HStack>
                    <Button size="sm" colorScheme="green" onClick={handleSaveSection}>
                      Save
                    </Button>
                    <Button size="sm" variant="outline" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                  </HStack>
                </VStack>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Name</Text>
                    <Text fontSize="sm" color={submission.phlebotomist_name ? "gray.800" : "red.500"}>
                      {submission.phlebotomist_name || <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                  <Box>
                    <Text fontSize="sm" fontWeight="medium" color="gray.600">Email</Text>
                    <Text fontSize="sm" color={submission.phlebotomist_email ? "gray.800" : "red.500"}>
                      {submission.phlebotomist_email || <Text as="span" fontStyle="italic">Missing</Text>}
                    </Text>
                  </Box>
                </SimpleGrid>
              )}
            </CardBody>
          </Card>
        </VStack>

        {/* ORIGINAL EDIT FORM - Keep all existing functionality */}
        <Box p={3} bg="gray.50" borderRadius="lg" border="1px solid" borderColor="gray.200">
          <HStack justify="space-between" mb={2}>
            <Heading size="md">Edit Submission</Heading>
            <Button colorScheme="blue" size="sm" onClick={handleSave} isLoading={saving}>
              Save Changes
            </Button>
          </HStack>
        {editingForm.lab_results_url && editingForm.status !== 'completed' && (
          <Button colorScheme="teal" mb={4} onClick={async () => {
            const { error } = await supabase.from('submissions').update({ status: 'completed' }).eq('id', submission.id);
            if (!error) {
              setEditingForm(f => ({ ...f, status: 'completed' }));
              toast({ title: 'Marked as completed', status: 'success', duration: 2000, isClosable: true });
            } else {
              toast({ title: 'Error', description: error.message, status: 'error', duration: 4000, isClosable: true });
            }
          }}>
            Mark as Completed
          </Button>
        )}
        <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={3}>
          <VStack spacing={3} align="stretch">
            <Box>
              <Heading size="sm" mb={1} color="blue.600">Patient Information</Heading>
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={1}>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Patient Name</FormLabel>
                  <Input name="patient_name" value={editingForm.patient_name || ''} onChange={handleChange} size="sm" maxW="250px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Patient Email</FormLabel>
                  <Input name="patient_email" value={editingForm.patient_email || ''} onChange={handleChange} size="sm" maxW="250px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Patient Address</FormLabel>
                  <Input name="patient_address" value={editingForm.patient_address || ''} onChange={handleChange} size="sm" maxW="250px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Patient DOB</FormLabel>
                  <Input name="patient_dob" value={editingForm.patient_dob || ''} onChange={handleChange} size="sm" maxW="150px" />
                </FormControl>
              </SimpleGrid>
            </Box>
            <Box>
              <Heading size="sm" mb={1} color="green.600">Doctor Information</Heading>
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={1}>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Doctor Name</FormLabel>
                  <Input name="doctor_name" value={editingForm.doctor_name || ''} onChange={handleChange} size="sm" maxW="250px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Doctor Email</FormLabel>
                  <Input name="doctor_email" value={editingForm.doctor_email || ''} onChange={handleChange} size="sm" maxW="250px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Doctor Phone</FormLabel>
                  <Input name="doctor_phone" value={editingForm.doctor_phone || ''} onChange={handleChange} size="sm" maxW="150px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Doctor Fax</FormLabel>
                  <Input name="doctor_fax" value={editingForm.doctor_fax || ''} onChange={handleChange} size="sm" maxW="150px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Doctor Address</FormLabel>
                  <Input name="doctor_address" value={editingForm.doctor_address || ''} onChange={handleChange} size="sm" maxW="250px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Client Name</FormLabel>
                  <Input name="client_name" value={editingForm.client_name || ''} onChange={handleChange} size="sm" maxW="250px" placeholder="Facility or client name" />
                </FormControl>
              </SimpleGrid>
            </Box>
            <Box>
              <Heading size="sm" mb={1} color="purple.600">Test Information</Heading>
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={1}>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Lab Brand</FormLabel>
                  <Input name="lab_brand" value={editingForm.lab_brand || ''} onChange={handleChange} size="sm" maxW="250px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Blood Collection Time</FormLabel>
                  <Input name="blood_collection_time" value={editingForm.blood_collection_time || ''} onChange={handleChange} size="sm" maxW="150px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Special Instructions</FormLabel>
                  <Box p={2} bg="gray.50" borderRadius="md" fontSize="sm" minH="32px">
                    {editingForm.special_instructions || <Text color="gray.400">No special instructions</Text>}
                  </Box>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Specimen</FormLabel>
                  <Box p={2} bg="gray.50" borderRadius="md" fontSize="sm" minH="32px">
                    {editingForm.specimen_type ? (() => {
                      try {
                        const specs = JSON.parse(editingForm.specimen_type);
                        return Array.isArray(specs) ? specs.map((s, i) => <Text key={i} fontSize="sm">{s.quantity}x {s.type}</Text>) : editingForm.specimen_type;
                      } catch { return editingForm.specimen_type; }
                    })() : <Text color="gray.400">No specimen</Text>}
                  </Box>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Diagnosis</FormLabel>
                  <Box p={2} bg="gray.50" borderRadius="md" fontSize="sm" minH="32px">
                    {editingForm.diagnosis || <Text color="gray.400">No diagnosis</Text>}
                  </Box>
                </FormControl>
              </SimpleGrid>
            </Box>
          </VStack>
          <VStack spacing={3} align="stretch">
            <Box>
              <Heading size="sm" mb={1} color="orange.600">Insurance Information</Heading>
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={1}>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Insurance Company</FormLabel>
                  <Input name="insurance_company" value={editingForm.insurance_company || ''} onChange={handleChange} size="sm" maxW="250px" />
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Insurance Policy Number</FormLabel>
                  <Input name="insurance_policy_number" value={editingForm.insurance_policy_number || ''} onChange={handleChange} size="sm" maxW="150px" />
                </FormControl>
              </SimpleGrid>
            </Box>
            <Box>
              <Heading size="sm" mb={1} color="red.600">Status & Actions</Heading>
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={1}>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Status</FormLabel>
                  <Select name="status" value={editingForm.status || ''} onChange={handleChange} size="sm" maxW="150px">
                    <option value="pending">Pending</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </Select>
                </FormControl>
                <HStack spacing={2} alignItems="flex-end">
                  <Button size="sm" colorScheme="blue" variant="outline" onClick={() => handleLabChange(submission)}>
                    Change Lab
                  </Button>
                  <Text fontSize="sm" color="gray.600">
                    Lab: {getLabName(submission.lab_id, submission)}
                  </Text>
                </HStack>
                <HStack spacing={2} alignItems="flex-end">
                  <Button
                    size="sm"
                    colorScheme={submission.phlebotomist_id ? 'purple' : 'red'}
                    variant="outline"
                    onClick={() => handlePhlebChange(submission)}
                  >
                    {submission.phlebotomist_id ? 'Change phleb' : 'Assign phleb'}
                  </Button>
                  <Text fontSize="sm" color={submission.phlebotomist_id ? 'gray.600' : 'red.500'} fontWeight={submission.phlebotomist_id ? 'normal' : '600'}>
                    Phleb: {submission.phlebotomist_id
                      ? (phlebotomists[submission.phlebotomist_id]?.full_name
                         || phlebotomists[submission.phlebotomist_id]?.company_name
                         || submission.phlebotomist_name
                         || 'Unknown')
                      : 'Unassigned'}
                  </Text>
                </HStack>
              </SimpleGrid>
            </Box>
            <Box>
              <Heading size="sm" mb={1} color="teal.600">Files & Documents</Heading>
              <SimpleGrid columns={{ base: 1, xl: 2 }} spacing={1}>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>FedEx Label</FormLabel>
                  <HStack spacing={2} flexWrap="wrap">
                    {editingForm.fedex_label_url && (
                      <Box textAlign="center">
                        <a href={editingForm.fedex_label_url} target="_blank" rel="noopener noreferrer">
                          {isImageFile(editingForm.fedex_label_url) ? (
                            <img src={editingForm.fedex_label_url} alt="FedEx Label" style={{ width: 300, height: 300, objectFit: 'cover', borderRadius: 6, marginBottom: 2, border: '1px solid #ccc' }} />
                          ) : editingForm.fedex_label_url && typeof editingForm.fedex_label_url === 'string' && editingForm.fedex_label_url.endsWith('.pdf') ? (
                            <PdfIcon />
                          ) : null}
                        </a>
                        <Button as="a" href={editingForm.fedex_label_url} target="_blank" colorScheme="blue" size="sm" leftIcon={<AttachmentIcon />}>View</Button>
                      </Box>
                    )}
                    <VStack spacing={2} align="stretch">
                      <Button
                        leftIcon={<FaShippingFast />}
                        colorScheme="orange"
                        size="sm"
                        onClick={() => handleGenerateFedexLabel(selectedSubmission)}
                        maxW="250px"
                      >
                        Generate FedEx Label
                      </Button>
                      <Input type="file" accept="application/pdf,image/*" onChange={e => handleFileUpload('fedex-labels', e.target.files[0])} size="sm" maxW="250px" />
                    </VStack>
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Lab Results</FormLabel>
                  <HStack spacing={2} flexWrap="wrap">
                    {editingForm.lab_results_url && (
                      <Box textAlign="center">
                        <a href={editingForm.lab_results_url} target="_blank" rel="noopener noreferrer">
                          {isImageFile(editingForm.lab_results_url) ? (
                            <img src={editingForm.lab_results_url} alt="Lab Results" style={{ width: 300, height: 300, objectFit: 'cover', borderRadius: 6, marginBottom: 2, border: '1px solid #ccc' }} />
                          ) : editingForm.lab_results_url && typeof editingForm.lab_results_url === 'string' && editingForm.lab_results_url.endsWith('.pdf') ? (
                            <PdfIcon />
                          ) : null}
                        </a>
                        <Button as="a" href={editingForm.lab_results_url} target="_blank" colorScheme="green" size="sm" leftIcon={<AttachmentIcon />}>View</Button>
                      </Box>
                    )}
                    <Input type="file" accept="application/pdf,image/*" onChange={e => handleFileUpload('lab-results', e.target.files[0])} size="sm" maxW="250px" />
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Doctor's Script</FormLabel>
                  <HStack spacing={2} flexWrap="wrap">
                    {Array.isArray(editingForm.script_image)
                      ? editingForm.script_image.map((url, idx) => (
                          <Box key={idx} textAlign="center">
                            <Box as="span" style={{ cursor: 'pointer', display: 'inline-block' }} onClick={() => openPreview(url)}>
                              {isImageFile(url) ? (
                                <img src={url} alt={`Script ${idx + 1}`} style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, marginBottom: 2, border: '1px solid #ccc' }} />
                              ) : url && typeof url === 'string' && url.endsWith('.pdf') ? (
                                <PdfIcon />
                              ) : null}
                            </Box>
                            <Button as="a" href={url} target="_blank" colorScheme="blue" size="sm" leftIcon={<AttachmentIcon />}>View {idx + 1}</Button>
                          </Box>
                        ))
                      : editingForm.script_image && (
                          <Box textAlign="center">
                            <Box as="span" style={{ cursor: 'pointer', display: 'inline-block' }} onClick={() => openPreview(editingForm.script_image)}>
                              {isImageFile(editingForm.script_image) ? (
                                <img src={editingForm.script_image} alt="Script" style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 6, marginBottom: 2, border: '1px solid #ccc' }} />
                              ) : editingForm.script_image && typeof editingForm.script_image === 'string' && editingForm.script_image.endsWith('.pdf') ? (
                                <PdfIcon />
                              ) : null}
                            </Box>
                            <Button as="a" href={editingForm.script_image} target="_blank" colorScheme="blue" size="sm" leftIcon={<AttachmentIcon />}>View</Button>
                          </Box>
                        )}
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Insurance Card</FormLabel>
                  <HStack spacing={2} flexWrap="wrap">
                    {(() => {
                      let insuranceCardImages = editingForm.insurance_card_image;
                      if (typeof insuranceCardImages === 'string') {
                        try {
                          insuranceCardImages = JSON.parse(insuranceCardImages);
                        } catch {
                          insuranceCardImages = [];
                        }
                      }
                      if (Array.isArray(insuranceCardImages)) {
                        return insuranceCardImages.map((url, idx) => (
                          <Box key={idx} textAlign="center">
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              {isImageFile(url) ? (
                                <img src={url} alt={`Insurance Card ${idx + 1}`} style={{ width: 300, height: 300, objectFit: 'cover', borderRadius: 6, marginBottom: 2, border: '1px solid #ccc' }} />
                              ) : url && typeof url === 'string' && url.endsWith('.pdf') ? (
                                <PdfIcon />
                              ) : null}
                            </a>
                            <Button as="a" href={url} target="_blank" colorScheme="purple" size="sm" leftIcon={<AttachmentIcon />}>
                              View {insuranceCardImages.length > 1 ? idx + 1 : ''}
                            </Button>
                          </Box>
                        ));
                      } else if (insuranceCardImages) {
                        return (
                          <Box textAlign="center">
                            <a href={insuranceCardImages} target="_blank" rel="noopener noreferrer">
                              {isImageFile(insuranceCardImages) ? (
                                <img src={insuranceCardImages} alt="Insurance Card" style={{ width: 300, height: 300, objectFit: 'cover', borderRadius: 6, marginBottom: 2, border: '1px solid #ccc' }} />
                              ) : insuranceCardImages && typeof insuranceCardImages === 'string' && insuranceCardImages.endsWith('.pdf') ? (
                                <PdfIcon />
                              ) : null}
                            </a>
                            <Button as="a" href={insuranceCardImages} target="_blank" colorScheme="purple" size="sm" leftIcon={<AttachmentIcon />}>
                              View
                            </Button>
                          </Box>
                        );
                      } else {
                        return null;
                      }
                    })()}
                  </HStack>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" mb={0.5}>Patient ID</FormLabel>
                  <HStack spacing={2} flexWrap="wrap">
                    {Array.isArray(editingForm.patient_id_image)
                      ? editingForm.patient_id_image.map((url, idx) => (
                          <Box key={idx} textAlign="center">
                            <a href={url} target="_blank" rel="noopener noreferrer">
                              {isImageFile(url) ? (
                                <img src={url} alt={`Patient ID ${idx + 1}`} style={{ width: 300, height: 300, objectFit: 'cover', borderRadius: 6, marginBottom: 2, border: '1px solid #ccc' }} />
                              ) : url && typeof url === 'string' && url.endsWith('.pdf') ? (
                                <PdfIcon />
                              ) : null}
                            </a>
                            <Button as="a" href={url} target="_blank" colorScheme="orange" size="sm" leftIcon={<AttachmentIcon />}>
                              View {idx + 1}
                            </Button>
                          </Box>
                        ))
                      : editingForm.patient_id_image && (
                          <Box textAlign="center">
                            <a href={editingForm.patient_id_image} target="_blank" rel="noopener noreferrer">
                              {isImageFile(editingForm.patient_id_image) ? (
                                <img src={editingForm.patient_id_image} alt="Patient ID" style={{ width: 300, height: 300, objectFit: 'cover', borderRadius: 6, marginBottom: 2, border: '1px solid #ccc' }} />
                              ) : editingForm.patient_id_image && typeof editingForm.patient_id_image === 'string' && editingForm.patient_id_image.endsWith('.pdf') ? (
                                <PdfIcon />
                              ) : null}
                            </a>
                            <Button as="a" href={editingForm.patient_id_image} target="_blank" colorScheme="orange" size="sm" leftIcon={<AttachmentIcon />}>
                              View
                            </Button>
                          </Box>
                        )}
                  </HStack>
                </FormControl>
              </SimpleGrid>
            </Box>
          </VStack>
        </Grid>
        </Box>
        
        <PreviewModal isOpen={isPreviewOpen} onClose={closePreview} fileUrl={previewUrl} />
      </Box>
    );
  };

  const SubmissionCard = ({ submission, isExpanded, onClick, isSelected, onSelect, onDelete, isDuplicate }) => {
    const fileCount = getFileCount(submission);
    const date = new Date(submission.submitted_at).toLocaleDateString();
    const { percent, filledCount, totalCount, firstMissingSection } = computeCompletion(submission);
    
    // Calculate fees - if in a batch, ship fee comes from batch, otherwise from submission
    const phlebotomist = submission.phlebotomist_id ? phlebotomists[submission.phlebotomist_id] : null;
    const rawDrawFee = phlebotomist?.lab_draw_fee !== null && phlebotomist?.lab_draw_fee !== undefined
      ? parseFloat(phlebotomist.lab_draw_fee)
      : 0;
    const drawFee = rawDrawFee;
    const adminFee = submission.admin_fee !== null && submission.admin_fee !== undefined 
      ? parseFloat(submission.admin_fee) 
      : 0;
    
    // Ship fee: if in batch, use batch ship_fee divided by batch size, otherwise use submission's fedex_shipping_cost
    let shipFee = 0;
    if (submission.batch_id) {
      const batch = batches.find(b => b.id === submission.batch_id);
      if (batch) {
        const batchSubmissions = submissions.filter(s => s.batch_id === batch.id);
        const batchShipFee = batch.ship_fee || 0;
        // Divide ship fee equally among submissions in batch (or show 0 if not set yet)
        shipFee = batchSubmissions.length > 0 ? parseFloat(batchShipFee) / batchSubmissions.length : 0;
      }
    } else {
      shipFee = submission.fedex_shipping_cost !== null && submission.fedex_shipping_cost !== undefined 
        ? parseFloat(submission.fedex_shipping_cost) 
        : 0;
    }
    
    const total = drawFee + adminFee + shipFee;

    // Get batch info (reuse from ship fee calc or look up)
    const batchInfo = submission.batch_id ? batches.find(b => b.id === submission.batch_id) : null;
    
    const formatCurrency = (value) => {
      if (value === null || value === undefined || isNaN(value)) return '$0.00';
      return `$${value.toFixed(2)}`;
    };
    
    return (
      <Tr
        _hover={{ bg: 'blue.50' }}
        cursor="pointer"
        onClick={(e) => {
          // Don't trigger row click if clicking on checkbox or checkbox cell
          const target = e.target;
          const isCheckbox = target.type === 'checkbox' || 
                           target.closest('input[type="checkbox"]') ||
                           target.closest('span[role="checkbox"]') ||
                           target.closest('label');
          
          // Check if click is in the last column (checkbox column)
          const cell = target.closest('td');
          if (cell && cell === e.currentTarget.querySelector('td:last-child')) {
            return;
          }
          
          if (isCheckbox) {
            return;
          }
          
          onClick();
        }}
        data-dup={isDuplicate ? '1' : undefined}
        bg={isExpanded ? 'blue.50' : (isDuplicate ? 'orange.50' : 'white')}
      >
        <Td>
          <Text fontSize="xs" color={formatSchedule(submission) ? 'gray.700' : 'gray.400'}>
            {formatSchedule(submission) || '—'}
          </Text>
        </Td>
        <Td>
          <VStack align="start" spacing={1}>
            <HStack spacing={2} flexWrap="wrap" rowGap={1}>
              {(() => {
                const cfg = submission.outcome_code ? getOutcomeConfig(submission.outcome_code) : null;
                return cfg
                  ? <Badge colorScheme={cfg.color} fontSize="xs">{cfg.label}</Badge>
                  : <Text fontSize="xs" color="gray.400">—</Text>;
              })()}
              {batchInfo && (
                <Badge colorScheme="purple" fontSize="xs">
                  {batchInfo.batch_number || 'Batch'}
                </Badge>
              )}
            </HStack>
            {submission.is_self_pay && (
              <Badge
                colorScheme={submission.payment_status === 'paid' ? 'green' : submission.payment_status === 'pending' ? 'yellow' : 'orange'}
                fontSize="10px"
                variant={submission.payment_status === 'paid' ? 'solid' : 'subtle'}
              >
                {submission.payment_status === 'paid' ? 'PAID' : 'Self Pay — Unpaid'}
              </Badge>
            )}
          </VStack>
        </Td>
        <Td whiteSpace="nowrap">
          {(() => {
            const info = getNextUpdateInfo(submission);
            if (info.state === 'done') return <Text fontSize="xs" color="gray.400">—</Text>;
            if (info.state === 'none') return <Badge colorScheme="gray" fontSize="2xs">No updates yet</Badge>;
            if (info.state === 'overdue') return <Badge colorScheme="red" fontSize="2xs">Overdue {fmtDur(info.dueMs)}</Badge>;
            const color = info.dueMs < 3600000 ? 'orange' : 'green';
            return <Badge colorScheme={color} fontSize="2xs">Due in {fmtDur(info.dueMs)}</Badge>;
          })()}
        </Td>
        <Td>
          <HStack spacing={1}>
            <Text fontWeight="bold" fontSize="sm">{formatPatientName(submission.patient_name) || 'N/A'}</Text>
            {isDuplicate && (
              <Badge colorScheme="orange" fontSize="2xs" title="Same patient + same date as another draw">DUP</Badge>
            )}
          </HStack>
          <Text fontSize="xs" color="gray.500">{submission.patient_email}</Text>
        </Td>
        <Td maxW="220px">
          <Text fontSize="xs" color="gray.600" noOfLines={2} title={submission.outcome_notes || ''}>
            {submission.outcome_notes || '—'}
          </Text>
        </Td>
        <Td minW="150px" maxW="230px">
          <Text fontSize="xs" color="gray.600">
            {submission.patient_address
              || [submission.patient_street, submission.patient_city, submission.patient_state, submission.patient_zip].filter(Boolean).join(', ')
              || '—'}
          </Text>
        </Td>
        <Td>
          {(() => {
            const src = getSubmissionSource(submission);
            return (
              <Badge colorScheme={src.colorScheme} fontSize="10px" title={src.title || ''}>
                {src.label}
              </Badge>
            );
          })()}
        </Td>
        <Td onClick={(e) => e.stopPropagation()}>
          {editingDateId === submission.id ? (
            <HStack spacing={1}>
              <Input
                type="date"
                size="xs"
                value={editingDateValue}
                onChange={(e) => setEditingDateValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') handleDateCancel(e);
                  if (e.key !== 'Tab' && e.key !== 'Escape') e.preventDefault();
                }}
                autoFocus
                w="130px"
                style={{ cursor: 'pointer' }}
                focusBorderColor="transparent"
                borderColor="transparent"
                _hover={{ borderColor: 'transparent' }}
              />
              <IconButton
                icon={<CheckCircleIcon />}
                size="xs"
                colorScheme="green"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  if (editingDateValue) {
                    const [y, m, d] = editingDateValue.split('-');
                    handleDateSaveDirect(submission.id, parseInt(m), parseInt(d), parseInt(y));
                  }
                }}
                aria-label="Save date"
              />
            </HStack>
          ) : (
            <HStack spacing={1}>
              <Text fontSize="xs" color="gray.600">{date}</Text>
              <IconButton
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'blue.500' }}
                onClick={(e) => handleDateEdit(submission, e)}
                aria-label="Edit date"
                minW="auto"
                h="auto"
                p={0}
              />
            </HStack>
          )}
        </Td>
        <Td onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
          {submission.phlebotomist_name ? (
            <HStack spacing={1}>
              <Text fontSize="xs" color="purple.600" fontWeight="medium">{submission.phlebotomist_name}</Text>
              <IconButton
                aria-label="Change phlebotomist"
                icon={<EditIcon />}
                size="xs"
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'purple.500' }}
                minW="auto"
                h="auto"
                p={0}
                onClick={() => handlePhlebChange(submission)}
              />
            </HStack>
          ) : (
            <Button
              size="xs"
              variant="outline"
              colorScheme="purple"
              onClick={() => handlePhlebChange(submission)}
            >
              + Assign phleb
            </Button>
          )}
        </Td>
        <Td>
          {Array.isArray(submission.tags) && submission.tags.length > 0 ? (
            <HStack spacing={1} flexWrap="wrap" rowGap={1}>
              {submission.tags.map((t) => (
                <Tag key={t} size="sm" colorScheme={tagColor(t)} borderRadius="full" fontSize="2xs">
                  {t}
                </Tag>
              ))}
            </HStack>
          ) : (
            <Text fontSize="xs" color="gray.400">—</Text>
          )}
        </Td>
        <Td>
          <Text fontSize="xs" color={submission.lab_id ? "blue.600" : "red.500"} fontWeight={submission.lab_id ? "medium" : "normal"}>
            {submission.lab_id ? getLabName(submission.lab_id, submission) : 'Not Assigned'}
          </Text>
        </Td>
        <Td>
          <Text fontSize="xs" color="gray.600">{submission.doctor_name || 'N/A'}</Text>
        </Td>
        <Td>
          <Text fontSize="xs" color="gray.600">{submission.client_name || '—'}</Text>
        </Td>
        <Td>
          <Text fontSize="xs" color="gray.600" fontFamily="mono">{submission.client_code || '—'}</Text>
        </Td>
        <Td>
          <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">
            {submission.outcome_logged_at
              ? new Date(submission.outcome_logged_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })
              : '—'}
          </Text>
        </Td>
        <Td>
          {finalizedAt[submission.id] ? (
            <Text fontSize="xs" color="green.600" fontWeight="medium" whiteSpace="nowrap">
              {new Date(finalizedAt[submission.id]).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}
            </Text>
          ) : (
            <Text fontSize="xs" color="gray.400">—</Text>
          )}
        </Td>
        <Td>
          <HStack>
            <Box bg="blue.600" color="white" borderRadius="full" fontSize="xs" fontWeight="bold" w="20px" h="20px" display="flex" alignItems="center" justifyContent="center">
              {fileCount}
            </Box>
          </HStack>
        </Td>
        <Td onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
          {onDelete && (
            <IconButton
              aria-label="Delete submission"
              icon={<DeleteIcon />}
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={(e) => onDelete(submission, e)}
            />
          )}
        </Td>
        <Td onClick={(e) => e.stopPropagation()} style={{ cursor: 'default' }}>
          <Checkbox
            isChecked={isSelected}
            onChange={(e) => {
              e.stopPropagation();
              onSelect(e.target.checked);
            }}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
            onMouseDown={(e) => e.stopPropagation()}
          />
        </Td>
      </Tr>
    );
  };

  // Dashboard Banner Component
  const DashboardBanner = ({ submissions }) => {
    const cardBg = useColorModeValue('white', 'gray.800');
    const cardShadow = useColorModeValue('md', 'dark-lg');

    const calculateStats = () => {
      const statusCounts = submissions.reduce((acc, submission) => {
        acc[submission.status] = (acc[submission.status] || 0) + 1;
        return acc;
      }, {});

      // Counts by latest disposition (outcome_code) for the dispositions row.
      const outcomeCounts = submissions.reduce((acc, s) => {
        if (s.outcome_code) acc[s.outcome_code] = (acc[s.outcome_code] || 0) + 1;
        return acc;
      }, {});
      const disp = {
        total: submissions.length,
        inProgress: statusCounts.in_progress || 0,
        drawn: outcomeCounts.successful_draw || 0,
        noAnswer: outcomeCounts.no_answer || 0,
        reschedule: outcomeCounts.reschedule_requested || 0,
        appointment: outcomeCounts.scheduled || 0,
        voicemail: outcomeCounts.voicemail_left || 0,
        dropped: outcomeCounts.dropped_off_lab || 0,
      };

      const selfPayUnpaid = submissions.filter(s => s.is_self_pay && s.payment_status !== 'paid');
      const selfPayPending = selfPayUnpaid.reduce((sum, s) => sum + (parseFloat(s.self_pay_amount) || 0), 0);

      // Day-based counts on the draw date (submitted_at, falling back to created_at).
      // Weeks run Sunday→Saturday; "this month" is from the 1st of the current month.
      const now = new Date();
      const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const startOfYesterday = new Date(startOfToday);
      startOfYesterday.setDate(startOfYesterday.getDate() - 1);
      const startOfThisWeek = new Date(startOfToday);
      startOfThisWeek.setDate(startOfToday.getDate() - startOfToday.getDay());
      const startOfLastWeek = new Date(startOfThisWeek);
      startOfLastWeek.setDate(startOfThisWeek.getDate() - 7);
      const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);

      const dayCounts = { today: 0, yesterday: 0, thisWeek: 0, lastWeek: 0, thisMonth: 0 };
      submissions.forEach((s) => {
        const raw = s.submitted_at || s.created_at;
        if (!raw) return;
        const d = new Date(raw);
        if (isNaN(d)) return;
        if (d >= startOfToday) dayCounts.today += 1;
        if (d >= startOfYesterday && d < startOfToday) dayCounts.yesterday += 1;
        if (d >= startOfThisWeek) dayCounts.thisWeek += 1;
        if (d >= startOfLastWeek && d < startOfThisWeek) dayCounts.lastWeek += 1;
        if (d >= startOfThisMonth) dayCounts.thisMonth += 1;
      });

      // Next-update SLA rollup across active draws + how many got a status
      // update logged today.
      let noUpdates = 0;
      let overdue = 0;
      let soonestDueMs = null; // smallest positive time-until-due among on-track draws
      let updatedToday = 0;
      let updatedLastHour = 0;
      const oneHourAgo = new Date(now.getTime() - 3600000);
      submissions.forEach((s) => {
        const info = getNextUpdateInfo(s);
        if (info.state === 'none') noUpdates += 1;
        else if (info.state === 'overdue') overdue += 1;
        else if (info.state === 'due') {
          if (soonestDueMs === null || info.dueMs < soonestDueMs) soonestDueMs = info.dueMs;
        }
        if (s.outcome_logged_at) {
          const lu = new Date(s.outcome_logged_at);
          if (!isNaN(lu)) {
            if (lu >= startOfToday) updatedToday += 1;
            if (lu >= oneHourAgo) updatedLastHour += 1;
          }
        }
      });

      return {
        pending: statusCounts.pending || 0,
        in_progress: statusCounts.in_progress || 0,
        selfPayUnpaidCount: selfPayUnpaid.length,
        selfPayPendingRevenue: selfPayPending,
        day: dayCounts,
        disp,
        sla: { noUpdates, overdue, soonestDueMs, updatedToday, updatedLastHour },
      };
    };

    const stats = calculateStats();
    const hasPendingDraws = stats.pending > 0;

    const handleStatusClick = (status) => {
      // Reset all filters first
      const newFilters = {
        pending: false,
        waiting_on_lab_results: false,
        in_progress: false,
        completed: false,
        cancelled: false
      };
      
      // Set only the clicked status to true
      newFilters[status] = true;
      setStatusFilters(newFilters);
    };

    return (
      <>
        {(() => {
          // Compact stat chips shown inside each dropdown's popover.
          const Chip = ({ value, label, color = 'gray', onClick, sub }) => (
            <HStack
              spacing={1.5}
              px={2}
              py={0.5}
              borderWidth="1px"
              borderColor={`${color}.200`}
              borderRadius="md"
              bg={`${color}.50`}
              cursor={onClick ? 'pointer' : 'default'}
              _hover={onClick ? { borderColor: `${color}.400`, boxShadow: 'sm' } : undefined}
              onClick={onClick}
            >
              <Text fontSize="xs" fontWeight="bold" color={`${color}.600`} whiteSpace="nowrap">{value}</Text>
              <Text fontSize="2xs" color="gray.600" whiteSpace="nowrap">{label}</Text>
              {sub && <Text fontSize="2xs" color={`${color}.500`} fontWeight="600">{sub}</Text>}
            </HStack>
          );
          // Each stat group is a dropdown button (matching the filter buttons);
          // the numbers live in the popover so the header stays on one row.
          const StatBtn = ({ title, badge, badgeColor = 'gray', first, children }) => (
            <Popover placement="bottom-end" isLazy>
              <PopoverTrigger>
                <Button size="xs" variant="outline" colorScheme="gray" ml={first ? 'auto' : undefined}>
                  {title} ▾
                  {badge != null && badge !== '' && (
                    <Badge ml={1.5} colorScheme={badgeColor} borderRadius="full">{badge}</Badge>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent w="auto" maxW="360px">
                <PopoverArrow />
                <PopoverBody>
                  <Wrap spacing={1.5} align="center">{children}</Wrap>
                </PopoverBody>
              </PopoverContent>
            </Popover>
          );
          return (
            <>
              <StatBtn title="New" badge={stats.day.thisWeek} badgeColor="blue" first>
                <Chip value={stats.day.today} label="today" color="green" />
                <Chip value={stats.day.thisWeek} label="this week" color="blue" />
                <Chip value={stats.day.thisMonth} label="this month" color="purple" />
              </StatBtn>
              <StatBtn title="Updates due" badge={stats.sla.overdue} badgeColor={stats.sla.overdue > 0 ? 'red' : 'gray'}>
                <Chip value={stats.sla.updatedLastHour} label="updated in the last hour" color="green" />
                <Chip value={stats.sla.updatedToday} label="updated today" color="green" />
                <Chip value={stats.sla.overdue} label="overdue" color="red" sub="filter" onClick={() => setOverdueOnly(true)} />
                <Chip value={stats.sla.noUpdates} label="never updated" color="gray" onClick={() => setOverdueOnly(false)} />
                {stats.sla.soonestDueMs === null ? (
                  <Chip value="✓" label="none due soon" color="green" />
                ) : (
                  <Chip value={fmtDur(stats.sla.soonestDueMs)} label="next due" color="green" />
                )}
              </StatBtn>
              <StatBtn title="By status">
                <Chip value={stats.pending} label="pending" color="yellow" onClick={() => handleStatusClick('pending')} />
                <Chip value={stats.in_progress} label="in progress" color="blue" onClick={() => handleStatusClick('in_progress')} />
                <Chip
                  value={stats.selfPayUnpaidCount}
                  label="self-pay unpaid"
                  color="teal"
                  sub={stats.selfPayPendingRevenue > 0 ? `$${stats.selfPayPendingRevenue.toFixed(2)}` : undefined}
                  onClick={() => setPaymentFilter('self_pay_unpaid')}
                />
              </StatBtn>
              <StatBtn title="Dispositions" badge={stats.disp.total} badgeColor="gray">
                <Chip value={stats.disp.total} label="patients" color="gray" />
                <Chip value={stats.disp.inProgress} label="in progress" color="blue" />
                <Chip value={stats.disp.drawn} label="drawn" color="cyan" />
                <Chip value={stats.disp.noAnswer} label="no answer" color="orange" />
                <Chip value={stats.disp.reschedule} label="reschedule" color="red" />
                <Chip value={stats.disp.appointment} label="appointment" color="green" />
                <Chip value={stats.disp.voicemail} label="voicemail left" color="purple" />
                <Chip value={stats.disp.dropped} label="dropped at lab" color="teal" />
              </StatBtn>
            </>
          );
        })()}
      </>
    );
  };

  if (loading) {
    return (
      <Box w="100%" bg="gray.50" minH="60vh">
        <Box w="100%" px={4} py={6}>
          <VStack spacing={4} align="stretch">
            <Card>
              <CardBody>
                <VStack spacing={3} py={10}>
                  <Spinner size="xl" thickness="4px" />
                  <Text fontWeight="semibold" color="gray.800">
                    Loading submissions…
                  </Text>
                  <Text fontSize="sm" color="gray.500" textAlign="center" maxW="420px">
                    Fetching the latest submissions, labs, and phlebotomist details.
                  </Text>
                </VStack>
              </CardBody>
            </Card>
          </VStack>
        </Box>
      </Box>
    );
  }

  return (
    <Box bg="gray.50" mx={-8} mt={-8} mb={-8} overflowX="hidden">
      <Box px={3} py={3} minW={0}>
          <VStack spacing={2} align="stretch" minW={0}>

            <Box>
                <VStack spacing={1.5} align="stretch">
                  {/* Filters collapsed into one row of dropdowns — each button
                      expands its options in a popover. */}
                  <HStack spacing={2} flexWrap="wrap" gap={2}>
                    <Popover placement="bottom-start" isLazy>
                      <PopoverTrigger>
                        <Button size="xs" variant="outline" colorScheme="gray">
                          Filter by Status ▾
                          {!Object.values(statusFilters).every(Boolean) && (
                            <Badge ml={1.5} colorScheme="blue" borderRadius="full">
                              {Object.values(statusFilters).filter(Boolean).length}
                            </Badge>
                          )}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent w="auto" maxW="520px">
                        <PopoverArrow />
                        <PopoverBody>
                          <Wrap spacing={2} align="center">
                            <WrapItem>
                              <Button size="xs" variant={Object.values(statusFilters).every(Boolean) ? "solid" : "outline"} colorScheme="gray" onClick={showAllStatuses}>
                                Show All
                              </Button>
                            </WrapItem>
                            {Object.entries(statusFilters).map(([status, isEnabled]) => (
                              <WrapItem key={status}>
                                <Button size="xs" variant={isEnabled ? "solid" : "outline"} colorScheme={getStatusColor(status)} onClick={() => toggleStatusFilter(status)} textTransform="capitalize">
                                  {status.replace(/_/g, ' ')}
                                </Button>
                              </WrapItem>
                            ))}
                          </Wrap>
                        </PopoverBody>
                      </PopoverContent>
                    </Popover>

                    {phlebsWithDraws.length > 0 && (
                      <Popover placement="bottom-start" isLazy>
                        <PopoverTrigger>
                          <Button size="xs" variant="outline" colorScheme={phlebotomistIdFilter ? 'purple' : 'gray'}>
                            Filter by Phlebotomist ▾
                            {phlebotomistIdFilter && <Badge ml={1.5} colorScheme="purple" borderRadius="full">1</Badge>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent w="auto" maxW="520px">
                          <PopoverArrow />
                          <PopoverBody maxH="300px" overflowY="auto">
                            <Wrap spacing={2} align="center">
                              <WrapItem>
                                <Button size="xs" variant={phlebotomistIdFilter ? 'outline' : 'solid'} colorScheme="gray" onClick={() => { setPhlebotomistIdFilter(null); setShowCompleted(false); }}>
                                  All
                                </Button>
                              </WrapItem>
                              {phlebsWithDraws.map((p) => (
                                <WrapItem key={p.id}>
                                  <Button size="xs" variant={phlebotomistIdFilter === p.id ? 'solid' : 'outline'} colorScheme="purple" onClick={() => { setShowCompleted(false); setPhlebotomistIdFilter((cur) => (cur === p.id ? null : p.id)); }}>
                                    {p.name}
                                    <Badge ml={2} colorScheme="purple" variant="subtle" borderRadius="full">{p.count}</Badge>
                                  </Button>
                                </WrapItem>
                              ))}
                              {phlebotomistIdFilter && (
                                <WrapItem>
                                  <Button size="xs" variant={showCompleted ? 'solid' : 'outline'} colorScheme="green" onClick={() => setShowCompleted((v) => !v)}>
                                    {showCompleted ? 'Hide completed' : 'Show completed'}
                                  </Button>
                                </WrapItem>
                              )}
                            </Wrap>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    )}

                    {availableTags.length > 0 && (
                      <Popover placement="bottom-start" isLazy>
                        <PopoverTrigger>
                          <Button size="xs" variant="outline" colorScheme={tagFilter ? 'blue' : 'gray'}>
                            Filter by Tag ▾
                            {tagFilter && <Badge ml={1.5} colorScheme="blue" borderRadius="full">1</Badge>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent w="auto" maxW="520px">
                          <PopoverArrow />
                          <PopoverBody maxH="300px" overflowY="auto">
                            <Wrap spacing={2} align="center">
                              <WrapItem>
                                <Button size="xs" variant={tagFilter ? 'outline' : 'solid'} colorScheme="gray" onClick={() => setTagFilter(null)}>
                                  All
                                </Button>
                              </WrapItem>
                              {availableTags.map(({ tag, count }) => (
                                <WrapItem key={tag}>
                                  <Button size="xs" variant={tagFilter === tag ? 'solid' : 'outline'} colorScheme={tagColor(tag)} onClick={() => setTagFilter((cur) => (cur === tag ? null : tag))}>
                                    {tag}
                                    <Badge ml={2} colorScheme={tagColor(tag)} variant="subtle" borderRadius="full">{count}</Badge>
                                  </Button>
                                </WrapItem>
                              ))}
                            </Wrap>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    )}

                    <Button
                      size="xs"
                      variant={ourClientsOnly ? 'solid' : 'outline'}
                      colorScheme={ourClientsOnly ? 'purple' : 'gray'}
                      onClick={() => setOurClientsOnly((v) => !v)}
                      title={`Only client codes ${Array.from(OUR_CLIENT_CODES).join(', ')}`}
                    >
                      Our Clients
                    </Button>

                    {availableClientCodes.length > 0 && (
                      <Popover placement="bottom-start" isLazy>
                        <PopoverTrigger>
                          <Button size="xs" variant="outline" colorScheme={clientCodeFilter ? 'purple' : 'gray'}>
                            Filter by Client Code ▾
                            {clientCodeFilter && <Badge ml={1.5} colorScheme="purple" borderRadius="full">1</Badge>}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent w="auto" maxW="520px">
                          <PopoverArrow />
                          <PopoverBody maxH="300px" overflowY="auto">
                            <Wrap spacing={2} align="center">
                              <WrapItem>
                                <Button size="xs" variant={clientCodeFilter ? 'outline' : 'solid'} colorScheme="gray" onClick={() => setClientCodeFilter(null)}>
                                  All
                                </Button>
                              </WrapItem>
                              {availableClientCodes.map(({ code, label, count }) => (
                                <WrapItem key={code}>
                                  <Button size="xs" variant={clientCodeFilter === code ? 'solid' : 'outline'} colorScheme="purple" onClick={() => setClientCodeFilter((cur) => (cur === code ? null : code))}>
                                    {label}
                                    <Badge ml={2} colorScheme="purple" variant="subtle" borderRadius="full">{count}</Badge>
                                  </Button>
                                </WrapItem>
                              ))}
                            </Wrap>
                          </PopoverBody>
                        </PopoverContent>
                      </Popover>
                    )}

                    <DashboardBanner submissions={filteredSubmissions} />
                    <Button size="xs" variant="outline" colorScheme="orange" leftIcon={<FaClone />}
                      onClick={() => navigate('/admin/duplicates')}>Duplicates</Button>
                    <Button size="xs" variant="outline" colorScheme="blue" leftIcon={<FaSync />}
                      onClick={() => navigate('/admin/lab-api')}>Lab API</Button>
                    <Button size="xs" variant="outline" colorScheme="teal" leftIcon={<CopyIcon />}
                      onClick={handleCopySummary}
                      title="Copy a text summary of the currently filtered list">
                      Copy Summary
                    </Button>
                  </HStack>

                  <Divider />

                  <HStack spacing={3} flexWrap="wrap">
                    <HStack
                      spacing={3}
                      flexShrink={0}
                      bg={sortMode === 'priority' ? 'blue.50' : 'gray.50'}
                      borderWidth="1px"
                      borderColor={sortMode === 'priority' ? 'blue.200' : 'gray.200'}
                      borderRadius="md"
                      px={3}
                      py={1}
                    >
                      <Button
                        size="xs"
                        colorScheme="blue"
                        variant="solid"
                        leftIcon={<Text as="span">↕</Text>}
                        onClick={() => setSortMode((m) => (m === 'priority' ? 'recent' : 'priority'))}
                      >
                        {sortMode === 'priority' ? 'Priority view' : 'Most recent'}
                      </Button>
                      <Text fontSize="xs" fontWeight="700" color="gray.700" whiteSpace="nowrap">
                        {sortMode === 'priority' ? 'Sorted by urgency' : 'Sorted by date'}
                      </Text>
                    </HStack>
                    <InputGroup size="sm" flex={1} minW={{ base: "220px", md: "320px" }}>
                      <InputLeftElement pointerEvents="none">
                        <SearchIcon color="gray.400" />
                      </InputLeftElement>
                      <Input
                        placeholder="Search patient, doctor, status, phleb, lab, address, or ZIP..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                      />
                    </InputGroup>
                    <Select
                      size="sm"
                      w="180px"
                      value={paymentFilter}
                      onChange={(e) => setPaymentFilter(e.target.value)}
                    >
                      <option value="all">All Payments</option>
                      <option value="self_pay_unpaid">Self Pay — Unpaid</option>
                      <option value="self_pay_paid">Self Pay — Paid</option>
                      <option value="insurance">Insurance Only</option>
                    </Select>
                    <Button
                      size="sm"
                      colorScheme="red"
                      variant={overdueOnly ? 'solid' : 'outline'}
                      onClick={() => setOverdueOnly((v) => !v)}
                    >
                      ⚠ Overdue updates
                    </Button>
                    {searchQuery && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setSearchQuery('')}
                      >
                        Clear
                      </Button>
                    )}
                  </HStack>

                  {/* Date range — filter by draw date or scheduled date. */}
                  <HStack spacing={2} flexWrap="wrap" align="center">
                    <Text fontSize="xs" fontWeight="600" color="gray.600">Date range</Text>
                    <Select size="xs" w="140px" value={dateField} onChange={(e) => setDateField(e.target.value)}>
                      <option value="draw">Draw date</option>
                      <option value="scheduled">Scheduled date</option>
                    </Select>
                    {(() => {
                      const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                      const setRange = (from, to) => { setDateFrom(from); setDateTo(to); };
                      const today = new Date();
                      const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
                      // Scheduled dates are usually upcoming, so offer forward-looking
                      // ranges there; draw date stays backward-looking.
                      const presets = dateField === 'scheduled'
                        ? [
                            { label: 'Today', from: ymd(startToday), to: ymd(startToday) },
                            { label: 'Next 7 days', from: ymd(startToday), to: ymd(new Date(startToday.getTime() + 6 * 864e5)) },
                            { label: 'Next 30 days', from: ymd(startToday), to: ymd(new Date(startToday.getTime() + 29 * 864e5)) },
                            { label: 'This month', from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(new Date(today.getFullYear(), today.getMonth() + 1, 0)) },
                          ]
                        : [
                            { label: 'Today', from: ymd(startToday), to: ymd(startToday) },
                            { label: '7 days', from: ymd(new Date(startToday.getTime() - 6 * 864e5)), to: ymd(startToday) },
                            { label: '30 days', from: ymd(new Date(startToday.getTime() - 29 * 864e5)), to: ymd(startToday) },
                            { label: 'This month', from: ymd(new Date(today.getFullYear(), today.getMonth(), 1)), to: ymd(startToday) },
                          ];
                      return presets.map((p) => (
                        <Button key={p.label} size="xs"
                          variant={dateFrom === p.from && dateTo === p.to ? 'solid' : 'outline'}
                          colorScheme="blue"
                          onClick={() => setRange(p.from, p.to)}>
                          {p.label}
                        </Button>
                      ));
                    })()}
                    <Input size="sm" type="date" w="150px" value={dateFrom} max={dateTo || undefined}
                      onChange={(e) => setDateFrom(e.target.value)} />
                    <Text fontSize="xs" color="gray.400">to</Text>
                    <Input size="sm" type="date" w="150px" value={dateTo} min={dateFrom || undefined}
                      onChange={(e) => setDateTo(e.target.value)} />
                    {(dateFrom || dateTo) && (
                      <Button size="xs" variant="ghost" onClick={() => { setDateFrom(''); setDateTo(''); }}>
                        Clear dates
                      </Button>
                    )}
                  </HStack>
                </VStack>
            </Box>

            {selectedSubmissions.size > 0 && (
                <Button
                  colorScheme="purple"
                  size="sm"
                  onClick={onBatchModalOpen}
                >
                  Add {selectedSubmissions.size} to Invoice
                </Button>
            )}

            <Box overflowX="auto" w="100%" minW={0}>
              <Table
                size="sm"
                variant="unstyled"
                sx={{
                  // Spreadsheet feel: thin gridlines, tight cells, small text,
                  // no wasted vertical space.
                  'th, td': {
                    borderWidth: '1px',
                    borderColor: 'gray.200',
                    px: 2,
                    py: 1,
                    fontSize: 'xs',
                    verticalAlign: 'middle',
                  },
                  th: {
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bg: 'gray.100',
                    textTransform: 'none',
                    letterSpacing: 'normal',
                    whiteSpace: 'nowrap',
                  },
                  // Freeze the first two columns (Scheduled + Status) so they
                  // stay put when scrolling right. Fixed widths make the second
                  // column's sticky offset deterministic. Body cells get an
                  // opaque bg; header corners sit above everything.
                  'td:first-of-type, th:first-of-type': {
                    position: 'sticky',
                    left: 0,
                    width: '150px',
                    minWidth: '150px',
                    maxWidth: '150px',
                    zIndex: 1,
                  },
                  'td:nth-of-type(2), th:nth-of-type(2)': {
                    position: 'sticky',
                    left: '150px',
                    zIndex: 1,
                    boxShadow: 'inset -1px 0 0 var(--chakra-colors-gray-300)',
                  },
                  'td:first-of-type, td:nth-of-type(2)': { bg: 'white' },
                  'th:first-of-type': { top: 0, zIndex: 3, bg: 'gray.100' },
                  'th:nth-of-type(2)': { top: 0, zIndex: 3, bg: 'gray.100' },
                  // Highlight the Patient column (4th) so it stands out while scrolling.
                  'td:nth-of-type(4), th:nth-of-type(4)': { bg: 'yellow.50' },
                  'tbody tr:hover td:nth-of-type(4)': { bg: 'yellow.100' },
                  // Duplicate rows (same patient + date) — tint the whole row,
                  // including the frozen first two cells.
                  'tbody tr[data-dup] td': { bg: 'orange.50' },
                  'tbody tr[data-dup] td:first-of-type, tbody tr[data-dup] td:nth-of-type(2)': { bg: 'orange.50' },
                  'tbody tr:hover td': { bg: 'blue.50' },
                  'tbody tr:hover td:first-of-type, tbody tr:hover td:nth-of-type(2)': { bg: 'blue.50' },
                  'tbody tr[data-dup]:hover td, tbody tr[data-dup]:hover td:first-of-type, tbody tr[data-dup]:hover td:nth-of-type(2)': { bg: 'orange.100' },
                }}
              >
                <Thead>
                  <Tr bg="gray.50">
                    <Th>Scheduled</Th>
                    <Th>Last Status</Th>
                    <Th>Next Update</Th>
                    <Th>Patient</Th>
                    <Th>Last Note</Th>
                    <Th>Address</Th>
                    <Th>Source</Th>
                    <Th>Date</Th>
                    <Th>Phlebotomist</Th>
                    <Th>Tag</Th>
                    <Th>Lab</Th>
                    <Th>Doctor</Th>
                    <Th>Client Name</Th>
                    <Th>Client Code</Th>
                    <Th>Status Updated</Th>
                    <Th>Finalized</Th>
                    <Th>Files</Th>
                    <Th>Actions</Th>
                    <Th>
                      <Checkbox
                        isChecked={selectedSubmissions.size > 0 && paginatedSubmissions.every(s => selectedSubmissions.has(s.id))}
                        isIndeterminate={selectedSubmissions.size > 0 && !paginatedSubmissions.every(s => selectedSubmissions.has(s.id)) && paginatedSubmissions.some(s => selectedSubmissions.has(s.id))}
                        onChange={(e) => {
                          if (e.target.checked) {
                            const newSelected = new Set([...selectedSubmissions, ...paginatedSubmissions.map(s => s.id)]);
                            setSelectedSubmissions(newSelected);
                          } else {
                            const newSelected = new Set([...selectedSubmissions].filter(id => !paginatedSubmissions.some(s => s.id === id)));
                            setSelectedSubmissions(newSelected);
                          }
                        }}
                      />
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedSubmissions.map((submission) => (
                    <SubmissionCard
                      key={submission.id}
                      submission={submission}
                      isExpanded={expandedId === submission.id}
                      isDuplicate={duplicateIds.has(submission.id)}
                      isSelected={selectedSubmissions.has(submission.id)}
                      onSelect={(checked) => {
                        const newSelected = new Set(selectedSubmissions);
                        if (checked) {
                          newSelected.add(submission.id);
                        } else {
                          newSelected.delete(submission.id);
                        }
                        setSelectedSubmissions(newSelected);
                      }}
                      onClick={() => {
                        window.open(`/admin/submissions/${submission.id}`, '_blank', 'noopener,noreferrer');
                      }}
                      onDelete={handleDeleteClick}
                    />
                  ))}
                </Tbody>
              </Table>
            </Box>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <Box mt={4} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={4}>
                <Text fontSize="sm" color="gray.600">
                  Showing {startIndex + 1} - {Math.min(endIndex, filteredSubmissions.length)} of {filteredSubmissions.length} submissions
                </Text>
                <HStack spacing={2}>
                  <Button
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    isDisabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    return (
                      <Button
                        key={pageNum}
                        size="sm"
                        onClick={() => setCurrentPage(pageNum)}
                        colorScheme={currentPage === pageNum ? 'blue' : 'gray'}
                        variant={currentPage === pageNum ? 'solid' : 'outline'}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button
                    size="sm"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    isDisabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </HStack>
              </Box>
            )}
          </VStack>
        </Box>

      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelDeleteRef}
        onClose={() => {
          onDeleteDialogClose();
          setSubmissionToDelete(null);
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Cancel submission?
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to cancel this submission? This will mark &quot;{submissionToDelete?.patient_name || 'this submission'}&quot; as cancelled. You can still view it in the list by filtering for Cancelled.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelDeleteRef}
                onClick={() => {
                  onDeleteDialogClose();
                  setSubmissionToDelete(null);
                }}
              >
                Keep
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} isLoading={deleteLoading} ml={3}>
                Cancel submission
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Change Lab for Submission</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Change the lab for submission: <strong>{selectedSubmission?.patient_name || 'Unknown'}</strong>
            </Text>
            <Select
              value={selectedLabId}
              onChange={(e) => setSelectedLabId(e.target.value)}
              placeholder="Select a lab"
            >
              {labs.map((lab) => (
                <option key={lab.id} value={lab.id}>
                  {lab.name}
                </option>
              ))}
            </Select>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleUpdateLab}
              isLoading={updatingLab}
              isDisabled={!selectedLabId}
            >
              Update Lab
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Phleb Assignment Modal */}
      <Modal isOpen={isPhlebModalOpen} onClose={onPhlebModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Phlebotomist</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4}>
              Submission: <strong>{selectedSubmission?.patient_name || 'Unknown'}</strong>
              {selectedSubmission?.draw_code ? ` (#${selectedSubmission.draw_code})` : ''}
            </Text>
            <Select
              value={selectedPhlebotomistId}
              onChange={(e) => setSelectedPhlebotomistId(e.target.value)}
              placeholder="— Unassigned —"
            >
              {Object.values(phlebotomists)
                .filter((p) => p && p.id)
                .sort((a, b) => (a.full_name || a.company_name || '').localeCompare(b.full_name || b.company_name || ''))
                .map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.full_name || p.company_name || p.email || p.id}
                    {p.lab_draw_fee != null ? ` — $${Number(p.lab_draw_fee).toFixed(2)}/draw` : ''}
                  </option>
                ))}
            </Select>
            <Text fontSize="xs" color="gray.500" mt={2}>
              Leave the picker empty (—Unassigned—) to clear the assignment.
              The rate snapshot used for billing comes from the phleb's profile at the time the weekly invoice is generated.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPhlebModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleUpdatePhleb}
              isLoading={updatingPhleb}
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Invoice Assignment Modal */}
      <Modal isOpen={isBatchModalOpen} onClose={onBatchModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Add Submissions to Invoice</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text>
                Add <strong>{selectedSubmissions.size}</strong> selected submission{selectedSubmissions.size !== 1 ? 's' : ''} to an invoice:
              </Text>
              <Select
                placeholder="Select an invoice"
                onChange={async (e) => {
                  const batchId = e.target.value;
                  if (batchId && batchId !== 'new') {
                    try {
                      const { error } = await supabase
                        .from('submissions')
                        .update({ batch_id: batchId })
                        .in('id', Array.from(selectedSubmissions));

                      if (error) throw error;

                      toast({
                        title: 'Success',
                        description: `Added ${selectedSubmissions.size} submission(s) to invoice`,
                        status: 'success',
                        duration: 3000,
                        isClosable: true,
                      });

                      setSelectedSubmissions(new Set());
                      fetchSubmissions();
                      fetchBatches();
                      onBatchModalClose();
                    } catch (error) {
                      console.error('Error adding submissions to invoice:', error);
                      toast({
                        title: 'Error',
                        description: error.message,
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                      });
                    }
                  } else if (batchId === 'new') {
                    // Create new batch
                    const batchName = prompt('Enter batch name:');
                    if (batchName) {
                      try {
                        // Generate batch number with retry on duplicate
                        let data, error, retries = 0;
                        while (retries < 3) {
                          const batchNumber = await generateBatchNumber();
                          const result = await supabase
                            .from('submission_batches')
                            .insert({
                              batch_name: batchName,
                              batch_number: batchNumber,
                              status: 'pending',
                            })
                            .select()
                            .single();
                          data = result.data;
                          error = result.error;
                          if (!error || !error.message?.includes('duplicate')) break;
                          retries++;
                        }

                        if (error) throw error;

                        // Add submissions to new batch
                        const { error: updateError } = await supabase
                          .from('submissions')
                          .update({ batch_id: data.id })
                          .in('id', Array.from(selectedSubmissions));

                        if (updateError) throw updateError;

                        toast({
                          title: 'Success',
                          description: `Created invoice and added ${selectedSubmissions.size} submission(s)`,
                          status: 'success',
                          duration: 3000,
                          isClosable: true,
                        });

                        setSelectedSubmissions(new Set());
                        fetchSubmissions();
                        fetchBatches();
                        onBatchModalClose();
                      } catch (error) {
                        console.error('Error creating invoice:', error);
                        toast({
                          title: 'Error',
                          description: error.message,
                          status: 'error',
                          duration: 3000,
                          isClosable: true,
                        });
                      }
                    }
                  }
                }}
              >
                <option value="new">+ Create New Invoice</option>
                {batches.map((batch) => (
                  <option key={batch.id} value={batch.id}>
                    {batch.batch_number || 'N/A'} - {batch.batch_name}
                  </option>
                ))}
              </Select>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" onClick={onBatchModalClose}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* FedEx Label Generator Modal */}
      <FedExLabelGenerator
        submissionId={selectedSubmissionForFedex?.id}
        isOpen={fedexModalOpen}
        onClose={handleFedexModalClose}
        onLabelGenerated={handleFedexLabelGenerated}
      />
    </Box>
  );
}

export default SubmissionsList; 